import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../utils/api';
import { InvoiceRow, ReceiptRow, QuotationRow } from '../../types';
import Pagination, { getPageSize } from '../Pagination';
import Fab from '../Fab';
import { tabColor, rowTint, lighten, darken, menuPill, TAB_GROUPS } from '../../tabPalette';
import ApprovalBadge from '../ApprovalBadge';
import DocNoLink from '../DocNoLink';
import MailBadge from '../MailBadge';
import { useBadgeOnly, BadgeFilterBar } from '../BadgeFilter';
import InvoiceForm from './InvoiceForm';
import ReceiptForm from './ReceiptForm';
import BulkInvoiceEmail from './BulkInvoiceEmail';
import InvoiceEmailThread from './InvoiceEmailThread';
import ReceiptEmailThread from './ReceiptEmailThread';
import ReceiptEmailComposer from './ReceiptEmailComposer';
import QuotationEmailThread from './QuotationEmailThread';
import QuotationEmailComposer from './QuotationEmailComposer';
import QuoSlipViewer, { SlipBadge } from './QuoSlipViewer';
import { CommentPlus, CommentPanel, AwaitingIcon } from '../Jobs';
import Contact from '../Contact';
import Services from '../Services';
import Bookkeeping from '../Bookkeeping';
import { useStore } from '../../store';
import { displayClientName } from '../../utils/displayName';

type Firm = { id: string; name: string; short: string };
type PeakTab = 'invoice' | 'quotation' | 'receipt' | 'contact' | 'services' | 'bookkeeping';

const fmtAmt = (n: number) => '฿' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// A receipt's displayed amount = the cash ACTUALLY received. In this firm's model the client transfers
// the FULL total (services + the WHT portion, which the firm remits for them), so the received amount
// already includes WHT — never add WHT on top of it (that would double-count).
const grossRc = (rc: any) => Number(rc.amount || 0);
const grossPaid = (r: any) => (r.receipts || []).reduce((s: number, rc: any) => s + grossRc(rc), 0);

// "Created by" cell (owner 2026-08-27): staff show as their name; non-human creators —
// automation ('Office'), client portal ('Portal'), future AI ('AI') — get a grey pill so a
// glance separates hand-made documents from generated ones.
const AUTO_CREATORS = ['Office', 'AI', 'Portal'];
function Creator({ label }: { label?: string }) {
  const v = (label || '').trim() || '—';
  if (AUTO_CREATORS.indexOf(v) >= 0) return (
    <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-secondary)', background: 'var(--bg-active)', border: '1px solid var(--border)', borderRadius: 999, padding: '1px 8px', whiteSpace: 'nowrap' }}>{v}</span>
  );
  return <span style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{v}</span>;
}

// Invoices, Quotations and Receipts hide the Created By column (Calem, report d7c20856: "allow admins
// (Liang, Calem, and Nick) only to open (unhide) those columns"). Bank Statement's extra columns work the
// same way (report 33249b9a): only management gets the toolbar chevron, the choice is remembered on this
// browser — one choice for all three lists — and the column shows only while management is logged in, so a
// choice another login left on the same browser never opens it for anyone else. The rows still carry
// created_by_label: a quotation's "Info Complete?" prompt names its approver from it.
// Management = admin, Calem or Nick — mirrors server/src/routes/files.ts isManagement, as FileLibrary does.
const MANAGEMENT_EMAILS = new Set(['calem@slf-legal.co', 'nick@slf-accounting.co']);
function useCreatorCol() {
  const me = useStore(s => s.me);
  const isMgmt = me?.role === 'admin' || MANAGEMENT_EMAILS.has(String(me?.email || '').toLowerCase());
  const [open, setOpen] = useState<boolean>(() => { try { return localStorage.getItem('pk_creatorCol') === '1'; } catch { return false; } });
  const toggle = () => setOpen(o => { const n = !o; try { localStorage.setItem('pk_creatorCol', n ? '1' : '0'); } catch { /* ignore */ } return n; });
  return { isMgmt, open, toggle, show: isMgmt && open };
}
function CreatorColButton({ col }: { col: ReturnType<typeof useCreatorCol> }) {
  const { t } = useTranslation();
  if (!col.isMgmt) return null;
  return (
    <button onClick={col.toggle} title={col.open ? t('pk_creator_col_hide') : t('pk_creator_col_show')} style={{ width: 32, height: 32, borderRadius: 'var(--radius-md)', border: 'none', background: 'transparent', color: col.open ? 'var(--accent)' : 'var(--text-secondary)', cursor: 'pointer' }}><i className={`ti ti-chevrons-${col.open ? 'left' : 'right'}`} style={{ fontSize: 15 }} aria-hidden /></button>
  );
}

// Company-tab counters (owner 2026-08-27): each All/Accounting/Legal tab showed how many
// documents in its scope are unpaid and how many are expired (quotation: past valid-until;
// invoice: past due date), computed over the current date window. Zero counts stay hidden.
// No list shows them now: Quotation's and Invoice's tabs dropped them (Calem, report 38417c66:
// "Remove this information from the quotation and invoice menu bubble"), and Receipt's tabs,
// which still call this, never get any — the receipt list sends no counts. The server still
// works them out for Quotation and Invoice.
function TabCounts({ c, t }: { c?: { unpaid: number; expired: number }; t: any }) {
  if (!c || (!c.unpaid && !c.expired)) return null;
  const chip: React.CSSProperties = { fontSize: 10, fontWeight: 800, borderRadius: 999, padding: '1px 7px', whiteSpace: 'nowrap' };
  return (<>
    {!!c.unpaid && <span style={{ ...chip, color: '#92400E', background: '#FEF3C7' }}>{t('pk_cnt_unpaid', { n: c.unpaid })}</span>}
    {!!c.expired && <span style={{ ...chip, color: '#B42318', background: '#FCEBEB' }}>{t('pk_cnt_expired', { n: c.expired })}</span>}
  </>);
}
const fmtDate = (ymd: string) => { const p = (ymd || '').split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : ymd; };
const firmLabel = (f: Firm) => f.name.includes('Legal') ? 'Legal' : f.name.includes('Accounting') ? 'Accounting' : (f.short || f.name);

// default date range = two months: 1st of LAST month → end of THIS month
// (covers the monthly auto-generated invoices and recent activity).
function defaultRange() {
  const d = new Date(); const y = d.getFullYear(), m = d.getMonth(); // m: 0-based
  const pad = (n: number) => String(n).padStart(2, '0');
  const fromD = new Date(y, m - 1, 1);                 // 1st of last month
  const from = `${fromD.getFullYear()}-${pad(fromD.getMonth() + 1)}-01`;
  const lastDay = new Date(y, m + 1, 0).getDate();     // last day of this month
  const to = `${y}-${pad(m + 1)}-${pad(lastDay)}`;
  return { from, to };
}

const STATUS: Record<string, { label: string; color: string; dot: string }> = {
  draft:   { label: 'pk_st_draft',   color: 'var(--text-tertiary)', dot: 'circle' },
  partial: { label: 'pk_st_partial', color: '#D97706',              dot: 'circle-half-2' },
  paid:    { label: 'pk_st_paid',    color: '#1D9E75',              dot: 'circle-check-filled' },
  expired: { label: 'pk_st_expired', color: '#E24B4A',              dot: 'circle-x' },
};

const fmtEpoch = (s?: number) => { if (!s) return ''; const d = new Date(s * 1000); const p = (n: number) => String(n).padStart(2, '0'); return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`; };

// Badge showing email activity for this document. Without onClick (quotation/receipt) it's the number
// of times we emailed it. With onClick (invoice) the count is the whole email-thread total (our sent
// copies + the client's replies) and clicking opens the thread viewer.
// How far the CLIENT has got confirming a quotation's lines on the public link. Only interactive
// quotations report a total, so older documents show nothing at all. Amber while partial, green when
// every line is confirmed — that is also the moment the payment details unlock for the client.
function ConfirmBadge({ done, total, accepted }: { done?: number; total?: number; accepted?: boolean }) {
  if (!total || total < 1) return null;
  const all = accepted || (done || 0) >= total;
  return (
    <span
      title={accepted ? 'Client has ACCEPTED this quotation — it is locked'
        : (done || 0) >= total ? 'Every line confirmed — waiting for the client to accept'
        : `Client has confirmed ${done || 0} of ${total} lines`}
      style={{
        marginLeft: 7, display: 'inline-flex', alignItems: 'center', gap: 4, verticalAlign: 'middle',
        fontSize: 11, fontWeight: 700, lineHeight: 1.6, padding: '1px 7px', borderRadius: 999,
        color: all ? '#0F6B49' : '#8A6212', background: all ? '#E6F6EF' : '#FEF6E0',
        border: `1px solid ${all ? '#B9E5D2' : '#F0DCA6'}`,
      }}
    >
      <i className={`ti ti-${accepted ? 'lock' : all ? 'circle-check' : 'progress'}`} style={{ fontSize: 11 }} aria-hidden />
      {accepted ? 'Accepted' : `${done || 0}/${total}`}
    </span>
  );
}

// Peak's ✉ badge now comes from the shared component (owner 2026-08-30) — same markup, one home.
const SentBadge = MailBadge;

// Just in front of the client name once the To: recipient has opened the quotation or invoice email
// (Calem, Bugify #bf7d6a54): the image in their copy loaded, or they followed a quotation's confirm
// link. Quotation-menu blue, as on the Portal tabs. A 19px circle, as AwaitingBadge's hourglass was then (Calem,
// Bugify #43e33304 3rd opening: "Make the viewing eye a circular bubble"); it was a wider pill. The ring
// is solid #185FA5, the blue family's darker step (ACCOUNT_COLORS.blue.hover), where the pill's was faint.
function OpenedBadge({ at }: { at?: number }) {
  const { t } = useTranslation();
  if (!at) return null;
  const when = new Date(at * 1000).toLocaleString('en-GB', { timeZone: 'Asia/Bangkok', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
  return (
    <span title={t('qo_opened_tip', { at: when })}
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 19, height: 19, borderRadius: '50%', color: '#378ADD', background: '#E6F1FB', border: '1px solid #185FA5', marginRight: 8, verticalAlign: 'middle' }}>
      <i className="ti ti-eye-check" style={{ fontSize: 13 }} aria-hidden />
    </span>
  );
}

// A quotation parked as "awaiting information" (Calem, Bugify #43e33304): an hourglass at the very front of
// the Client cell. It was an "AWAITING INFO" pill after the doc number, which widened the Doc No. column and
// took width from the Client column. The bare icon, 15px, in the Jobs menu's pink #EC4899 — Calem 2026-09-28
// (Bugify #37d916aa, 2nd opening: "Take the hourglass out of the circle, make it 2 px taller, and pink"); it
// was 13px inside a 19px blue circle like OpenedBadge's (#1D4ED8 ring on #EFF6FF). The icon is Calem's symbol
// since report 22f86d4d, 4th opening ("use the same symbol to replace the hourglass for approvals and
// invoices"): the hourglass inside two circular arrows that leads an awaiting level in Jobs (AwaitingIcon),
// at the same 15px and pink; it was Tabler's plain hourglass.
function AwaitingBadge({ note }: { note?: string }) {
  const { t } = useTranslation();
  return (
    <span title={note || t('qo_awaiting')}
      style={{ display: 'inline-flex', alignItems: 'center', color: '#EC4899', marginRight: 8, verticalAlign: 'middle' }}>
      <AwaitingIcon size={15} />
    </span>
  );
}

// The last cell of Quotations, Invoices and Receipts (Calem, Bugify #708bbb27): "keep the email and number
// vertically aligned and also keep the three vertical dots aligned down the page". The cell was centred
// with nothing at a fixed width, so the envelope, its number and the ⋮ moved sideways from row to row: the
// ✉ badge widens with its digits and is absent on a row never emailed, and an approval or "Info complete"
// button sits beside the ⋮ or in its place. Now every row lays out the same slots — the badge left-aligned
// in a fixed-width slot, the row's own button (if any) after it, and the ⋮ in a fixed-width slot pinned to
// the right edge. An empty slot keeps its width.
// The quotation's "Info Complete?" button has since moved to the Client cell (#708bbb27 reopened).
const ACT_MAIL_W = 48;   // room for a two-digit ✉ badge
const ACT_MENU_W = 26;   // RowMenu's ⋮ button
const ACT_GAP = 6;
// Header width = both slots + the gap + the cell's 12px padding each side (border-box). A row with a
// middle button is wider: the table then widens the whole column, and the slots still line up.
const ACT_COL_W = ACT_MAIL_W + ACT_GAP + ACT_MENU_W + 24;
function ActionCell({ mail, mid, menu }: { mail?: React.ReactNode; mid?: React.ReactNode; menu?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: ACT_GAP }}>
      <div style={{ width: ACT_MAIL_W, flexShrink: 0, display: 'flex', fontVariantNumeric: 'tabular-nums' }}>{mail}</div>
      {mid}
      <div style={{ width: ACT_MENU_W, flexShrink: 0, display: 'flex', marginLeft: 'auto' }}>{menu}</div>
    </div>
  );
}

// ── Each document a card (Calem, Bugify #6eb5a0b8) ─────────────────────────────────────────────
// "Make each of the line items (quote, invoice, receipt) in these tabs a bubble, following the style of Tracking and
// Active Jobs but, the same colors as the quotation/invoice receipt bubble (in top menu)". The three lists were flat
// tables, their rows one run of blue split by thin grey lines. Each document is now a rounded card, built the way the
// Jobs lists build theirs (cardLine there): the top menu's Quotation / Invoice / Receipt box made a card. Its fill stays
// the box's wash one shade lighter (each list's plainBg, see rowTint), and a row coloured for a reason keeps its colour
// — an expired quotation's red, an invoice's unpaid pink, a ticked invoice. The card's edge is the box's own line,
// TAB_GROUPS.peak.border, 1px as on the box, with the Tracking card's 10px corners. The cells draw it: down the first
// cell's left and the last cell's right on every row of the card, across the top of its first row and the bottom of its
// last, rounded where they meet (cardEdge). So the receipts listed under an invoice or a quotation sit inside its card,
// as the jobs waiting on a job sit inside the job's, and rows inside one card have no line between them. Cards stand
// CARD_GAP apart and CARD_INSET in from the list's edges — the table's padding — on the list's colour, set on the scroll
// box so it runs on under the last card, and the whole page's, round the list too: white, --bg-surface (Calem, report
// ddf1ba0e, 3rd opening: "Change every page background to white"), as Closed Jobs went first. It was the list's grey,
// --c-gray-50 (Bugify #b07c8dae, 2nd opening), and for that report's 1st opening all the top menu's blue one shade
// lighter, the plain cards' own fill. The first card stands HEAD_GAP under the column headings, as the Jobs list's
// UNKNOWN FINISH DATE bubble does. The edge is a shade darker than the box's line since that 3rd opening ("darken up the
// bubble border on each page by one shade"): 0.51 → 0.63, a quarter of the way on to the solid #378ADD (see darken), so
// on white it goes #99C3EE → #81B5EA. The box, the All / Accounting / Legal pills and the search box keep the box's
// line. Taken from the palette, so it follows the box.
const CARD_R = 10;
const CARD_GAP = 4;
const HEAD_GAP = 12;
const CARD_INSET = 17;
const cardLine = `1px solid ${darken(TAB_GROUPS.peak.border, 1)}`;
// A row's share of its card's edge, `first` / `last` being its place in the card: `cell` goes on every cell of the row,
// `edgeL` on its first cell as well and `edgeR` on its last.
function cardEdge(td: React.CSSProperties, first: boolean, last: boolean): { cell: React.CSSProperties; edgeL: React.CSSProperties; edgeR: React.CSSProperties } {
  return {
    cell: { ...td, ...(first ? { borderTop: cardLine } : {}), borderBottom: last ? cardLine : 'none' },
    edgeL: { borderLeft: cardLine, ...(first ? { borderTopLeftRadius: CARD_R } : {}), ...(last ? { borderBottomLeftRadius: CARD_R } : {}) },
    edgeR: { borderRight: cardLine, ...(first ? { borderTopRightRadius: CARD_R } : {}), ...(last ? { borderBottomRightRadius: CARD_R } : {}) },
  };
}
// The page above each card, and a strip more under the last one. See-through by a colour set on the row, not by having
// none: global.css's zebra shades every other row of a list with a header and gives way only to a colour on the row
// itself, so a gap that fell on an even row would come out a band of grey.
function CardGap({ cols, h = CARD_GAP }: { cols: number; h?: number }) {
  return <tr aria-hidden="true" style={{ background: 'transparent' }}><td colSpan={cols} style={{ padding: 0, height: h, border: 'none' }} /></tr>;
}
// Hover keeps the row's fill and rings it, where it turned the row white: the box's solid blue, TAB_GROUPS.peak.color —
// the selected Quotation / Invoice / Receipt tab's fill — 1px inside the row's edge, as the Jobs lists ring a job in
// their box's pink (ringRow there). Inset shadows on the row's cells draw it, so it bends round the card's corners and
// moves nothing. A receipt listed under an invoice or a quotation has no ring: its row opens nothing.
function ringRow(tr: HTMLTableRowElement, on: boolean) {
  const blue = TAB_GROUPS.peak.color;
  const cells = Array.from(tr.cells);
  cells.forEach((c, i) => {
    const edges = [`inset 0 1px 0 0 ${blue}`, `inset 0 -1px 0 0 ${blue}`];
    if (i === 0) edges.push(`inset 1px 0 0 0 ${blue}`);
    if (i === cells.length - 1) edges.push(`inset -1px 0 0 0 ${blue}`);
    c.style.boxShadow = on ? edges.join(', ') : '';
  });
}
// A document's number is that blue too (Calem, report 490ba173: "Make all the quote, invoice, receipt numbers/codes …
// the same color blue in keeping with the top menu color theme"): Doc No. on all three lists, the number of each receipt
// listed under an invoice or a quotation, and the Receipt list's For Document. They were greens — the app's accent
// #1D9E75 (Invoice's and Quotation's Doc No., For Document), #1FA971 (Receipt's Doc No.) and teal #0EA5A5 (the receipts
// under an invoice or a quotation). A deleted receipt's stay grey.
// Each opens its document too (DocNoLink, Calem, Bugify #b4cccf02: "They should all be linked (clickable pop-up) back to
// the original quotation, invoice, or receipt"): a row's own number opens the row's form as the row does, For Document
// its quotation or invoice, and a receipt under an invoice or a quotation its receipt form, where it opened the PDF.
const docNoColor = TAB_GROUPS.peak.color;
// The header's colour and its thin grey line carried on over the table's CARD_INSET padding to the list's edges,
// leftwards from the first header cell and rightwards from the last, as on the Jobs and Tracking lists (thEdge,
// thEdgeFillL/R there): the cards come in, the header doesn't. Inside a header cell, so it stays on the header as the
// list scrolls under it; it reaches a pixel below the cell's inside (bottom −1), so its line meets the cell's.
// The header's colour is the page's own, as the Jobs lists' header is — each list's th and this alike, so the band is
// one colour from edge to edge: white, --bg-surface, since the page went white (Calem, report ddf1ba0e, 3rd opening —
// see cardLine). It was the list's grey, --c-gray-50 (Calem, report c0d8d666: "Revert to background light gray color
// for all menus that look similar to this"), and the near-white before that. The list under it is that same colour,
// so the band's thin line is what sets the headings apart from it. That line is the darker --border-strong now, Active
// Jobs' own (Calem, report 4bd4ae4e — see the Invoice list's th).
const thEdge: React.CSSProperties = { position: 'absolute', top: 0, bottom: -1, width: CARD_INSET, background: 'var(--bg-surface)', borderBottom: '1px solid var(--border-strong)' };

// ── Invoice list ──────────────────────────────────────────────────────────
// ── Folded years (owner 2026-09-19) ─────────────────────────────────────────────────────────────
// "invoices, bills and receipts collapsible on the 31st of December each year — they are for
// monthly accounting and will not be valid for the next financial year."
//
// The fold happens on the SERVER, because these lists are paginated there: hiding old rows in the
// browser would only hide whichever ones landed on the page you are looking at, and page 2 would
// still be full of them. So a folded year is not on screen at all — which is exactly why each one
// gets a bar saying how many documents are behind it. Clicking re-asks the server for that year.
//
// A document with no payment has no year and never folds, so nothing owed can hide in here.
function YearFolds({ folded, open, onToggle }: {
  folded: { year: string; n: number }[]; open: string[]; onToggle: (y: string) => void;
}) {
  const { t } = useTranslation();
  if (!folded?.length) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, margin: '8px 0 2px' }}>
      {folded.map(f => {
        const isOpen = open.includes(f.year);
        return (
          <button key={f.year} type="button" onClick={() => onToggle(f.year)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
              padding: '8px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12.5, fontWeight: 700,
              border: '1px dashed var(--border-strong)',
              background: isOpen ? 'var(--bg-active)' : 'transparent',
              color: isOpen ? 'var(--accent)' : 'var(--text-secondary)',
            }}>
            <i className={`ti ti-chevron-${isOpen ? 'down' : 'right'}`} style={{ fontSize: 14 }} />
            <span>{f.year}</span>
            <span style={{ color: 'var(--text-tertiary)', fontWeight: 600 }}>{t('pk_folded_n', { n: f.n })}</span>
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 11, color: 'var(--text-tertiary)', fontWeight: 600 }}>
              {isOpen ? t('pk_fold_hide') : t('pk_fold_show')}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ── The list toolbars: ONE row, never two (owner 2026-09-25) ─────────────────
// Invoice's "Payment reminder" and "Monthly billing" dropped onto a second row as soon as the
// filters filled the width — a whole row of the list spent on two buttons, sitting where nobody
// looks for them. The row no longer wraps. The filters keep their natural width and scroll inside
// their own box if the window gets small; the ACTIONS at the end are measured, and as many as fit
// stay as buttons while the rest fold into one "More" menu at the end of the row. Quotation and
// Receipt are built from the same bar, so the rule holds wherever an action is added next.
//
// Measured rather than switched at a breakpoint: the filters' width depends on how many firms this
// user can see (the tabs held unpaid / expired counts too, until report 38417c66 took them off), so
// no fixed number would stay right for long. Every width is read off the elements themselves — a
// folded action stays in the DOM, parked off-screen, so it can still be measured — and the count
// only ever settles on what actually fits.
type BarMenuItem = { key: string; label: string; hint?: string; icon?: string; danger?: boolean; onClick: () => void };
type BarAction = {
  key: string; label: string; icon: string;
  tone?: 'accent' | 'amber';
  title?: string; busy?: boolean;
  onClick?: () => void;      // a plain action…
  menu?: BarMenuItem[];      // …or one that opens a menu of its own
};

const BAR_TONE: Record<string, { border: string; color: string }> = {
  accent: { border: 'var(--accent)', color: 'var(--accent)' },
  amber: { border: '#D97706', color: '#B45309' },
};

// One row of a dropdown — the label, and underneath it what choosing it will actually do. The
// descriptions are why the month-level actions live in a menu at all: a create-only action and a
// rebuild-everything action must never be two look-alike buttons side by side.
function BarMenuRow({ m, last, onDone }: { m: BarMenuItem; last?: boolean; onDone: () => void }) {
  return (
    <button onClick={() => { onDone(); m.onClick(); }}
      style={{ display: 'block', width: '100%', textAlign: 'left', padding: '11px 14px', border: 'none', borderBottom: last ? 'none' : '1px solid var(--border)', background: 'transparent', cursor: 'pointer' }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: m.danger ? '#B45309' : 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
        {m.icon && <i className={`ti ${m.icon}`} style={{ fontSize: 14, color: m.danger ? '#B45309' : 'var(--accent)' }} />}{m.label}
      </div>
      {m.hint && <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 3, lineHeight: 1.45 }}>{m.hint}</div>}
    </button>
  );
}

const barPanel: React.CSSProperties = { position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 41, width: 330, background: 'var(--bg-panel)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-md)', overflow: 'hidden' };

function BarActionButton({ a }: { a: BarAction }) {
  const [open, setOpen] = useState(false);
  const tone = BAR_TONE[a.tone || 'accent'];
  return (
    <div style={{ position: 'relative' }}>
      <button onClick={() => { if (a.busy) return; if (a.menu) setOpen(o => !o); else a.onClick?.(); }} title={a.title} disabled={a.busy}
        style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', borderRadius: 'var(--radius-md)', border: `1px solid ${tone.border}`, background: 'transparent', color: tone.color, cursor: a.busy ? 'default' : 'pointer', fontSize: 12.5, fontWeight: 600, opacity: a.busy ? 0.6 : 1, whiteSpace: 'nowrap' }}>
        <i className={`ti ${a.busy ? 'ti-loader-2' : a.icon}`} style={{ fontSize: 14, animation: a.busy ? 'spin 0.9s linear infinite' : undefined }} />
        {a.label}
        {a.menu && <i className="ti ti-chevron-down" style={{ fontSize: 12 }} />}
      </button>
      {a.menu && open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
          <div style={barPanel}>
            {a.menu.map((m, i) => <BarMenuRow key={m.key} m={m} last={i === a.menu!.length - 1} onDone={() => setOpen(false)} />)}
          </div>
        </>
      )}
    </div>
  );
}

// The folded actions. A plain one becomes a row; one that carries its own menu keeps that menu's
// rows, under its name — nothing is reduced to a bare label on the way in here.
function BarOverflow({ actions, innerRef }: { actions: BarAction[]; innerRef: React.RefObject<HTMLDivElement> }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rows: React.ReactNode[] = [];
  actions.forEach((a, ai) => {
    if (a.menu) {
      rows.push(
        <div key={a.key} style={{ borderTop: ai ? '1px solid var(--border)' : 'none' }}>
          <div style={{ padding: '9px 14px 2px', fontSize: 10.5, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>{a.label}</div>
          {a.menu.map((m, i) => <BarMenuRow key={m.key} m={m} last={i === a.menu!.length - 1} onDone={() => setOpen(false)} />)}
        </div>,
      );
    } else {
      rows.push(<BarMenuRow key={a.key} m={{ key: a.key, label: a.label, icon: a.icon, hint: a.title, onClick: () => a.onClick?.() }} last={ai === actions.length - 1} onDone={() => setOpen(false)} />);
    }
  });
  return (
    <div ref={innerRef} style={{ position: 'relative', flexShrink: 0 }}>
      <button onClick={() => setOpen(o => !o)} title={t('eml_nav_more')} aria-label={t('eml_nav_more')}
        style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 11px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap' }}>
        <i className="ti ti-dots" style={{ fontSize: 15 }} aria-hidden />{t('eml_nav_more')}
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
          <div style={barPanel}>{rows}</div>
        </>
      )}
    </div>
  );
}

const BAR_OFFSCREEN: React.CSSProperties = { position: 'absolute', left: -99999, top: 0, visibility: 'hidden', pointerEvents: 'none' };

function PeakToolbar({ filters, right, actions = [] }: { filters: React.ReactNode; right?: React.ReactNode; actions?: BarAction[] }) {
  const rowRef = useRef<HTMLDivElement | null>(null);
  const filRef = useRef<HTMLDivElement | null>(null);
  const rigRef = useRef<HTMLDivElement | null>(null);
  const moreRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const n = actions.length;
  const [shown, setShown] = useState(n);

  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!row) return;
    const GAP = 8, PAD = 32;   // the row's own left+right padding
    const measure = () => {
      const avail = row.clientWidth - PAD;
      // scrollWidth, not offsetWidth: the filters box may already be scrolling, and what matters is
      // the width it WANTS. Below that the box scrolls on its own and the row still holds one line.
      const base = (filRef.current?.scrollWidth || 0) + (rigRef.current?.offsetWidth || 0) + GAP * 2;
      const w = itemRefs.current.slice(0, n).map(el => el?.offsetWidth || 0);
      const moreW = moreRef.current?.offsetWidth || 86;
      const need = (k: number) => base + w.slice(0, k).reduce((sum, x) => sum + x + GAP, 0) + (k < n ? moreW + GAP : 0);
      let k = n;
      while (k > 0 && need(k) > avail) k--;
      setShown(prev => (prev === k ? prev : k));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(row);
    if (filRef.current) ro.observe(filRef.current);
    return () => ro.disconnect();
  }, [n]);

  // The row is the light grey, --c-gray-50, on Quotation, Invoice and Receipt, as File Storage's toolbar band is and
  // every top-menu page's now (Calem, report 3e769579, 4th opening: "Only the top menu strip on every page turns that
  // same grey then the rest of the page below this menu has a white background"). It was the page's white,
  // --bg-surface; the list under it stays white. Its line is a shade darker, --border-strong where it was --border, as
  // is the top menu's over it (Calem, report a903bda5, 3rd opening: "Make the gray horizontal lines one shade darker on
  // all page menus like this").
  return (
    <div ref={rowRef} style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: 8, padding: '8px 16px', borderBottom: '1px solid var(--border-strong)', background: 'var(--c-gray-50)' }}>
      <div ref={filRef} className="pk-bar-filters" style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flexShrink: 1, overflowX: 'auto' }}>{filters}</div>
      <div style={{ flex: 1, minWidth: 0 }} />
      {right && <div ref={rigRef} style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>{right}</div>}
      {n > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          {actions.map((a, i) => (
            <div key={a.key} ref={el => { itemRefs.current[i] = el; }} style={i < shown ? undefined : BAR_OFFSCREEN}>
              <BarActionButton a={a} />
            </div>
          ))}
          {shown < n && <BarOverflow actions={actions.slice(shown)} innerRef={moreRef} />}
        </div>
      )}
    </div>
  );
}

// The Date from–to boxes, on the right just before the search box and dressed as it is (Calem, report e1c9b137): round
// ends, its 1px line, 6px 14px and 12px semibold, in the page's top-menu blue from menuPill — its pale wash, its line
// and blue words — with the 'Date' label and the dash in the blue as well. They were on the left among the filters as
// square grey boxes: 8px corners, the faint grey outline, on the app's beige, --bg-app. Invoice's and Quotation's status
// dropdown stays on the left, as Receipt's 'Show deleted' does. PeakToolbar counts the right-hand group when it folds
// actions into More, so the boxes are counted there now.
function DateRange({ k, from, to, setFrom, setTo }: { k: string; from: string; to: string; setFrom: (v: string) => void; setTo: (v: string) => void }) {
  const { t } = useTranslation();
  const box: React.CSSProperties = { padding: '6px 14px', fontSize: 12, fontWeight: 600, borderRadius: 999, border: '1px solid', outline: 'none', ...menuPill(k, false) };
  return (<>
    <span style={{ fontSize: 11, color: tabColor(k) }}>{t('pk_date')}</span>
    <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={box} />
    <span style={{ color: tabColor(k) }}>–</span>
    <input type="date" value={to} onChange={e => setTo(e.target.value)} style={box} />
  </>);
}

export function InvoiceList({ initialOpen }: { initialOpen?: string } = {}) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<InvoiceRow[]>([]);
  const [firms, setFirms] = useState<Firm[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [company, setCompany] = useState('all');
  const [status, setStatus] = useState('all');
  const [q, setQ] = useState('');
  const dr = defaultRange();
  const [from, setFrom] = useState(dr.from);
  const [to, setTo] = useState(dr.to);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [formId, setFormId] = useState<string | null | false>(false); // false=closed, null=new, string=edit
  const [receiptFor, setReceiptFor] = useState<InvoiceRow | null>(null); // issue-receipt target
  const [rcEmail, setRcEmail] = useState<{ id: string; doc_no: string; client_name: string } | null>(null);   // nested-receipt email composer
  const [rcThread, setRcThread] = useState<{ id: string; doc_no: string; client_name: string } | null>(null); // nested-receipt email thread
  const [selected, setSelected] = useState<Set<string>>(new Set());     // bulk-email selection
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkMode, setBulkMode] = useState<'fee' | 'reminder'>('fee');   // which bulk-email flavour the dialog runs
  const [threadFor, setThreadFor] = useState<InvoiceRow | null>(null);  // email-thread viewer target
  const [fillBusy, setFillBusy] = useState(false);   // "Fill missing" in flight — block a second click

  const { badgeOnly, clearBadge } = useBadgeOnly();
  const [isApprover, setIsApprover] = useState(false);
  const [pageSize, setPageSize] = useState(getPageSize());
  const creatorCol = useCreatorCol();   // Created By, hidden unless management opens it (report d7c20856)
  // Years folded away, and the ones the operator has opened. Session-only on purpose: every visit
  // starts with the old years put away, which is the point of the fold (owner 2026-09-19).
  const [folded, setFolded] = useState<{ year: string; n: number }[]>([]);
  const [openYears, setOpenYears] = useState<string[]>([]);
  const toggleYear = (y: string) => setOpenYears(prev => prev.includes(y) ? prev.filter(x => x !== y) : [...prev, y]);
  const load = async (p = page, comp = company, st = status) => {
    setLoading(true); setErr(''); setSelected(new Set());
    try {
      const res = await api.getInvoices({ page: p, company: comp, status: st, from, to, badge: badgeOnly || undefined, limit: pageSize, years: openYears.join(',') });
      setRows(res.rows); setTotal(res.total); setPage(res.page); setFirms(res.companies || []); setFolded((res as any).folded || []); setIsApprover(!!(res as any).isApprover);
    } catch (e: any) { setErr(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(1, company, status); }, [company, status, from, to, badgeOnly, pageSize, openYears]); // eslint-disable-line

  // Delete a n invoice — blocked when it has receipt(s); always double-confirmed.
  const delInvoice = async (r: InvoiceRow, e: React.MouseEvent) => {
    e.stopPropagation();
    if ((r.receipts || []).length) { window.alert(t('pk_del_blocked', { no: r.doc_no })); return; }
    if (!window.confirm(t('pk_del_confirm', { no: r.doc_no }))) return;
    if (!window.confirm(t('pk_del_confirm2', { no: r.doc_no }))) return;
    try { await api.deleteInvoice(r.id); load(); } catch (e2: any) { window.alert(e2.message); }
  };
  // Rebuild a DRAFT invoice from the client's current services (e.g. after adding a recurring fee).
  const [regenId, setRegenId] = useState<string | null>(null);
  const regenInvoice = async (r: InvoiceRow, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(t('iv_regen_confirm', { no: r.doc_no }))) return;
    setRegenId(r.id);
    try { await api.regenerateInvoice(r.id); load(); }
    catch (e2: any) { window.alert(e2.message); }
    finally { setRegenId(null); }
  };
  useEffect(() => { if (initialOpen) setFormId(initialOpen); }, [initialOpen]);

  const filtered = rows.filter(r => !q.trim() || [r.doc_no, r.client_name].join(' ').toLowerCase().includes(q.trim().toLowerCase()));
  const allSel = filtered.length > 0 && filtered.every(r => selected.has(r.id));
  const someSel = filtered.some(r => selected.has(r.id));
  const toggleAll = () => setSelected(allSel ? new Set() : new Set(filtered.map(r => r.id)));
  const toggleOne = (id: string) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // The grey headings a point larger, 11 → 12px (Calem, report 66c9d174: "Increase the gray column headings … in
  // Quotations/Invoice/Receipts by 1 pt"), as Petty Cash's went (report e981f781). Quotation's and Receipt's below too.
  // Then Active Jobs' headings, as every staff list's: black, --text-primary, at the regular weight, where they were the
  // light grey --text-tertiary at semibold (600), on one 16px line, over the darker --border-strong line (Calem, report
  // 4bd4ae4e, 2nd opening: "Make all menus in the SLF system look like this: the same height, text color, no bold,
  // horizontal line colors etc."). The 12px stays; the band is 37px, as every list's is. Quotation's and Receipt's too.
  const th: React.CSSProperties = { textAlign: 'left', fontSize: 12, fontWeight: 400, color: 'var(--text-primary)', padding: '10px 12px', lineHeight: '16px', whiteSpace: 'nowrap', borderBottom: '1px solid var(--border-strong)', position: 'sticky', top: 0, background: 'var(--bg-surface)' };   // the page's white (report ddf1ba0e — see thEdge)
  const td: React.CSSProperties = { fontSize: 13, color: 'var(--text-primary)', padding: '10px 12px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' };
  // A plain row is the top menu's blue, one shade lighter (Calem, Bugify #1a85b4b0 and #37d916aa — see rowTint), not
  // transparent. The unpaid-7-days pink, a ticked row and a receipt's line under its invoice keep their own colours.
  const plainBg = rowTint('invoice', 1)!;
  const cols = creatorCol.show ? 11 : 10;   // a gap row spans them all (CardGap)
  // All / Accounting / Legal wear the top menu's blue Quotation…Approvals box (Calem, Bugify #667b55cb — see menuPill):
  // the selected one its solid blue #378ADD with white words, the rest its pale wash with blue words and its line. Were
  // the app's green, and white with a grey outline. Quotation's and Receipt's below are the same.
  // Just the name now: the unpaid / expired counts after it are gone (Calem, report 38417c66: "Remove this information
  // from the quotation and invoice menu bubble" — see TabCounts). Quotation's went too.
  const tab = (key: string, label: string) => (
    <button key={key} onClick={() => setCompany(key)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 999, border: '1px solid', fontSize: 12, fontWeight: 600, cursor: 'pointer', ...menuPill('invoice', company === key) }}>{label}</button>
  );

  // The page — round the cards, and under the badge strip and the folded years — is white, --bg-surface (Calem, report
  // ddf1ba0e, 3rd opening — see cardLine). It was the list's grey, --c-gray-50 (Bugify #b07c8dae, 2nd opening), which
  // was the scroll box's alone, the app's beige showing elsewhere, until that report's 1st opening made the lot the top
  // menu's blue one shade lighter.
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, background: 'var(--bg-surface)' }}>
      {badgeOnly && <BadgeFilterBar count={total} labelKey="bf_approvals" onClear={clearBadge} />}
      <PeakToolbar
        filters={<>
          {tab('all', t('pk_all'))}
          {firms.map(f => tab(f.id, firmLabel(f)))}
          <select value={status} onChange={e => setStatus(e.target.value)} style={{ padding: '6px 10px', fontSize: 12, borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--bg-app)', color: 'var(--text-primary)', marginLeft: 4 }}>
            <option value="all">{t('pk_all_status')}</option>
            <option value="unpaid">{t('pk_st_unpaid')}</option>
            {['draft', 'partial', 'paid'].map(s => <option key={s} value={s}>{t(STATUS[s].label)}</option>)}
          </select>
        </>}
        right={<>
          <DateRange k="invoice" from={from} to={to} setFrom={setFrom} setTo={setTo} />
          {/* The search box is an All / Accounting / Legal pill, unselected (Calem, Bugify #cba01676, 6th opening: "Do
              the same (colored bubble search) for quotation/invoice/receipts, email/calendar and petty cash"), as Bank
              Statement's is one of its period pills: round ends, their 1px line, 6px 14px and 12px semibold, so it stands
              as tall as they do — the magnifier 14px in, where their words start, and the words 32px in, clear of it, as
              on the Jobs lists, whose queue pills are cut the same — in the blue box's colours from menuPill: its pale
              wash, its line and blue words, the placeholder's too (mf-ph-inherit), with the magnifier in the blue. Its
              place, its width and its filtering are as they were. It was an 8px-cornered box with the faint grey outline
              on the app's beige, --bg-app, its grey magnifier 10px in and its words 28px. Quotation's and Receipt's below
              are the same. */}
          <div style={{ position: 'relative' }}>
            <i className="ti ti-search" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: tabColor('invoice') }} aria-hidden />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('pk_search')} className="mf-ph-inherit" style={{ width: 200, padding: '6px 14px 6px 32px', fontSize: 12, fontWeight: 600, borderRadius: 999, border: '1px solid', outline: 'none', ...menuPill('invoice', false) }} />
          </div>
          <button onClick={() => load()} title={t('refresh')} style={{ width: 32, height: 32, borderRadius: 'var(--radius-md)', border: 'none', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}><i className="ti ti-refresh" style={{ fontSize: 15 }} aria-hidden /></button>
          <CreatorColButton col={creatorCol} />
        </>}
        actions={[
          {
            // One click: select every invoice in the current view that has NO receipt yet (a receipt
            // = the client paid) and still owes money, then open the payment-reminder bulk flow.
            key: 'remind', label: t('iv_remind'), icon: 'ti-bell-dollar', tone: 'amber', title: t('iv_remind_hint'),
            onClick: () => {
              const unpaid = filtered.filter(r => (!r.receipts || r.receipts.length === 0) && Number(r.balance) > 0);
              if (!unpaid.length) { alert(t('iv_remind_none')); return; }
              setSelected(new Set(unpaid.map(r => r.id)));
              setBulkMode('reminder'); setBulkOpen(true);
            },
          },
          {
            // Both month-level actions live under ONE control: a safe create-only action and a
            // destructive rebuild sitting adjacent as look-alikes is exactly how the wrong one gets
            // clicked. The menu states what each one does at the moment of choosing.
            key: 'monthly', label: t('iv_monthly'), icon: 'ti-calendar-plus', tone: 'accent', title: t('iv_fill_hint'), busy: fillBusy,
            menu: [
              {
                key: 'fill', label: t('iv_fill'), hint: t('iv_fill_hint'), icon: 'ti-playlist-add',
                onClick: async () => {
                  if (fillBusy) return;
                  setFillBusy(true);
                  try {
                    const r: any = await api.generateMissingInvoices();
                    const created = r.created || 0, skipped = r.skipped || 0;
                    alert(created === 0 && skipped === 0 ? t('iv_gen_none')
                      : created === 0 ? t('iv_fill_none')
                      : t('iv_fill_result').replace('{c}', String(created)).replace('{s}', String(skipped)));
                    load();
                  } catch (e: any) { alert(e.message); }
                  finally { setFillBusy(false); }
                },
              },
              {
                key: 'rebuild', label: t('iv_gen'), hint: t('iv_gen_hint'), icon: 'ti-refresh-alert', danger: true,
                onClick: async () => {
                  if (!window.confirm(t('iv_gen_confirm'))) return;
                  try {
                    const r: any = await api.generateMonthlyInvoices();
                    const total = (r.created || 0) + (r.updated || 0) + (r.skipped || 0);
                    alert(total === 0 ? t('iv_gen_none')
                      : t('iv_gen_result').replace('{c}', r.created || 0).replace('{u}', r.updated || 0).replace('{s}', r.skipped || 0));
                    load();
                  } catch (e: any) { alert(e.message); }
                },
              },
            ],
          },
        ]}
      />
      {selected.size > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '11px 16px', background: 'linear-gradient(90deg, var(--accent), #2563EB)', color: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.12)', borderBottom: '1px solid var(--border)' }}>
          <i className="ti ti-checkbox" style={{ fontSize: 18 }} aria-hidden />
          <span style={{ fontWeight: 700, fontSize: 13.5 }}>{t('iv_bulk_selected').replace('{n}', String(selected.size))}</span>
          <button onClick={() => { setBulkMode('fee'); setBulkOpen(true); }} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 20px', borderRadius: 10, border: 'none', background: '#fff', color: 'var(--accent)', cursor: 'pointer', fontSize: 14, fontWeight: 800, boxShadow: '0 2px 6px rgba(0,0,0,0.18)' }}>
            <i className="ti ti-mail-forward" style={{ fontSize: 17 }} aria-hidden />{t('iv_bulk_email')}
          </button>
          <button onClick={() => { setBulkMode('reminder'); setBulkOpen(true); }} title={t('iv_remind_hint')} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 20px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.8)', background: 'transparent', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 800 }}>
            <i className="ti ti-bell-dollar" style={{ fontSize: 17 }} aria-hidden />{t('iv_remind_send')}
          </button>
          <div style={{ flex: 1 }} />
          <button onClick={() => setSelected(new Set())} style={{ padding: '7px 14px', borderRadius: 9, border: '1px solid rgba(255,255,255,0.6)', background: 'transparent', color: '#fff', cursor: 'pointer', fontSize: 12.5, fontWeight: 700 }}>{t('iv_bulk_clear')}</button>
        </div>
      )}

      {/* The page's white, as on the Jobs lists: between the cards, down either side of them and on under the last
          one (see cardLine). */}
      <div style={{ flex: 1, overflow: 'auto', background: 'var(--bg-surface)' }}>
        {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '10px 16px' }}>{err}</div>}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><i className="ti ti-loader" style={{ fontSize: 24, color: 'var(--text-tertiary)' }} aria-hidden /></div>
        ) : (
          // Separate borders with no spacing, as on the Jobs and Tracking lists: collapsed borders can't be rounded.
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, padding: `0 ${CARD_INSET}px` }}>
            <thead><tr>
              <th style={{ ...th, width: 36, textAlign: 'center' }}>
                <span aria-hidden style={{ ...thEdge, left: -CARD_INSET }} />
                <input type="checkbox" checked={allSel} ref={el => { if (el) el.indeterminate = someSel && !allSel; }} onChange={toggleAll} style={{ cursor: 'pointer' }} />
              </th>
              <th style={th}>{t('pk_doc_no')}</th><th style={th}>{t('pk_client')}</th>
              <th style={th}>{t('pk_issue')}</th><th style={th}>{t('pk_due')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('pk_amount')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('pk_paid')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('pk_balance')}</th>
              <th style={th}>{t('pk_status')}</th>
              {creatorCol.show && <th style={th}>{t('pk_created_by')}</th>}
              <th style={{ ...th, width: ACT_COL_W }}><span aria-hidden style={{ ...thEdge, right: -CARD_INSET }} /></th>
            </tr></thead>
            <tbody>
              {filtered.map((r, i) => {
                const s = STATUS[r.status] || STATUS.draft;
                // Pink wash (owner 2026-08-25): unpaid for MORE THAN 7 DAYS since issue — money is
                // sitting out there and the row should not look calm. Solid #F6E1E8, what the see-through
                // rgba(236,72,153,0.12) showed on the list's grey (Bugify #b07c8dae, 2nd opening). Pinned while
                // the 1st opening had that page blue, which turned it lilac, and kept now the page is white
                // (report ddf1ba0e), where the wash would come out paler.
                const unpaid7 = r.status !== 'paid' && (r.balance || 0) > 0 && r.issue_date
                  && (Date.now() - new Date(r.issue_date + 'T00:00:00').getTime()) > 7 * 86400000;
                const rowBg = unpaid7 ? '#F6E1E8' : plainBg;
                // The invoice's card (see cardEdge): its row opens it, and closes it too unless receipts follow.
                const { cell, edgeL, edgeR } = cardEdge(td, true, !(r.receipts || []).length);
                return (
                  <React.Fragment key={r.id}>
                  <CardGap cols={cols} h={i ? CARD_GAP : HEAD_GAP} />
                  <tr style={{ cursor: 'pointer', background: selected.has(r.id) ? 'var(--accent-bg)' : rowBg }} onClick={() => setFormId(r.id)}
                    onMouseEnter={e => ringRow(e.currentTarget, true)}
                    onMouseLeave={e => ringRow(e.currentTarget, false)}>
                    <td style={{ ...cell, ...edgeL, textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleOne(r.id)} style={{ cursor: 'pointer' }} />
                    </td>
                    <td style={{ ...cell, color: docNoColor, fontWeight: 600 }}><DocNoLink no={r.doc_no} onOpen={() => setFormId(r.id)} />{(!!(r as any).pending_approval || (!!(r as any).approval_required && !(r as any).approval_ok)) && <ApprovalBadge notifiedAt={Number((r as any).approval_notified_at) || 0} notifiedTo={(r as any).approval_notified_to} />}</td>
                    {/* Titles, middle names and a company's legal form off (Calem, report 101b5c69) — the PDF and the email keep them. */}
                    <td style={cell}><OpenedBadge at={r.opened_at} />{displayClientName(r.client_name, r.client_first, r.client_last) || '—'}</td>
                    <td style={{ ...cell, color: 'var(--text-secondary)' }}>{fmtDate(r.issue_date)}</td>
                    <td style={{ ...cell, color: 'var(--text-secondary)' }}>{fmtDate(r.due_date) || '—'}</td>
                    <td style={{ ...cell, textAlign: 'right', fontWeight: 600 }}>{fmtAmt(r.total_amount || r.net_to_pay)}</td>
                    <td style={{ ...cell, textAlign: 'right', color: grossPaid(r) > 0 ? '#1D9E75' : 'var(--text-tertiary)' }}>{grossPaid(r) > 0 ? fmtAmt(grossPaid(r)) : '—'}</td>
                    <td style={{ ...cell, textAlign: 'right', fontWeight: 600, color: r.balance > 0 ? '#D97706' : 'var(--text-tertiary)' }}>{r.balance > 0 ? fmtAmt(r.balance) : '—'}</td>
                    <td style={cell}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, fontWeight: 600, color: s.color }}><i className={`ti ti-${s.dot}`} style={{ fontSize: 12 }} aria-hidden />{t(s.label)}</span></td>
                    {creatorCol.show && <td style={cell}><Creator label={(r as any).created_by_label} /></td>}
                    <td style={{ ...cell, ...edgeR }}>
                      <ActionCell
                        mail={<SentBadge count={r.email_thread_count ?? r.email_sent_count} last={r.email_last_sent_at} onClick={() => setThreadFor(r)} />}
                        mid={isApprover && !!(r as any).approval_required && !(r as any).approval_ok && (
                          <button onClick={async e => { e.stopPropagation(); try { const lk: any = await api.invoiceApprovalLink(r.id); window.open(lk.url, '_blank'); } catch (e2: any) { window.alert(e2.message || 'Failed'); } }}
                            title={t('qo_appr_open')} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 8, border: 'none', background: '#B45309', color: '#fff', fontWeight: 800, fontSize: 11.5, cursor: 'pointer' }}>
                            <i className="ti ti-external-link" style={{ fontSize: 12 }} />{t('qo_appr_open')}
                          </button>
                        )}
                        menu={<RowMenu title={t('pk_actions')} actions={[
                          ...(r.status === 'draft' && !(r.receipts || []).length ? [{ label: t('iv_regen'), icon: 'refresh', busy: regenId === r.id, onClick: () => regenInvoice(r, { stopPropagation: () => {} } as any) }] : []),
                          ...(r.status !== 'paid' ? [{ label: t('rc_issue'), icon: 'receipt', onClick: () => setReceiptFor(r) }] : []),
                          ...(!(r.receipts || []).length ? [{ label: t('delete_short'), icon: 'trash', danger: true, onClick: () => delInvoice(r, { stopPropagation: () => {} } as any) }] : []),
                        ]} />}
                      />
                    </td>
                  </tr>
                  {(r.receipts || []).map((rc, j, all) => {
                    // Inside the invoice's card; the last receipt closes it.
                    const k = cardEdge(td, false, j === all.length - 1);
                    return (
                    <tr key={rc.id} style={{ background: '#F7FAFB' }}>
                      <td style={{ ...k.cell, ...k.edgeL }} />
                      <td style={{ ...k.cell, paddingLeft: 28, color: 'var(--text-tertiary)' }} colSpan={4}>
                        <i className="ti ti-corner-down-right" style={{ marginRight: 6 }} aria-hidden />
                        <DocNoLink no={rc.doc_no} id={rc.id} kind="receipt" style={{ fontWeight: 600 }} />
                        <span style={{ marginLeft: 10 }}>{fmtDate(rc.payment_date || rc.issue_date)}</span>
                      </td>
                      <td style={{ ...k.cell, textAlign: 'right', color: '#1D9E75', fontWeight: 600 }}>{fmtAmt(grossRc(rc))}</td>
                      {/* Under Paid · Balance · Status, and Created By when it shows. */}
                      <td colSpan={creatorCol.show ? 4 : 3} style={{ ...k.cell, color: 'var(--text-tertiary)', fontSize: 11 }}>{t('rc_receipt')}</td>
                      <td style={{ ...k.cell, ...k.edgeR }}>
                        <ActionCell
                          mail={<SentBadge count={rc.email_sent_count} last={rc.email_last_sent_at} onClick={() => setRcThread({ id: rc.id, doc_no: rc.doc_no, client_name: r.client_name })} />}
                          menu={<RowMenu title={t('pk_actions')} actions={[
                            { label: t('iv_email'), icon: 'send', onClick: () => setRcEmail({ id: rc.id, doc_no: rc.doc_no, client_name: r.client_name }) },
                            { label: 'PDF', icon: 'file-type-pdf', onClick: () => { api.openReceiptPdf(rc.id).catch(() => {}); } },
                            { label: t('delete_short'), icon: 'trash', danger: true, onClick: async () => { await deleteReceiptFlow(rc.id, load); } },
                          ]} />}
                        />
                      </td>
                    </tr>
                    );
                  })}
                  </React.Fragment>
                );
              })}
              {filtered.length > 0 && <CardGap cols={cols} />}
              {filtered.length === 0 && (
                <tr><td style={{ ...td, textAlign: 'center', color: 'var(--text-tertiary)', padding: 48 }} colSpan={creatorCol.show ? 11 : 10}>{t('pk_empty')}</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      <YearFolds folded={folded} open={openYears} onToggle={toggleYear} />
      <Pagination page={page} total={total} limit={pageSize} onPage={p => load(p)} onLimit={n => setPageSize(n)} />
      <Fab onClick={() => setFormId(null)} title={t('iv_new')} color={tabColor('invoice')} />
      {formId !== false && <InvoiceForm invoiceId={formId} onClose={() => setFormId(false)} onSaved={() => load()} />}
      {receiptFor && <ReceiptForm seed={{ parentType: 'invoice', parent: receiptFor }} onClose={() => setReceiptFor(null)} onSaved={() => load()} />}
      {rcEmail && <ReceiptEmailComposer receiptId={rcEmail.id} defaultName={rcEmail.client_name} onClose={() => setRcEmail(null)} onSent={() => load()} />}
      {rcThread && <ReceiptEmailThread receiptId={rcThread.id} docNo={rcThread.doc_no} clientName={rcThread.client_name} onClose={() => setRcThread(null)} />}
      {bulkOpen && (
        <BulkInvoiceEmail
          mode={bulkMode}
          invoices={filtered.filter(r => selected.has(r.id))
            .filter(r => bulkMode !== 'reminder' || ((!r.receipts || r.receipts.length === 0) && Number(r.balance) > 0))
            .map(r => ({ id: r.id, client_name: r.client_name }))}
          onClose={() => setBulkOpen(false)}
          onDone={() => { setSelected(new Set()); load(); }}
        />
      )}
      {threadFor && <InvoiceEmailThread invoiceId={threadFor.id} docNo={threadFor.doc_no} clientName={threadFor.client_name} onClose={() => setThreadFor(null)} />}
    </div>
  );
}

// ── Quotation list (like invoices, with Valid Until + Expired status) ───────
export function QuotationList({ initialOpen }: { initialOpen?: string } = {}) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<QuotationRow[]>([]);
  const [firms, setFirms] = useState<Firm[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [company, setCompany] = useState('all');
  const [status, setStatus] = useState('all');
  const [q, setQ] = useState('');
  const dr = defaultRange();
  const [from, setFrom] = useState(dr.from);
  const [to, setTo] = useState(dr.to);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [formId, setFormId] = useState<string | null | false>(false);
  const [receiptFor, setReceiptFor] = useState<QuotationRow | null>(null);
  const [rcEmail, setRcEmail] = useState<{ id: string; doc_no: string; client_name: string } | null>(null);   // nested-receipt email composer
  const [rcThread, setRcThread] = useState<{ id: string; doc_no: string; client_name: string } | null>(null); // nested-receipt email thread
  const [quoEmail, setQuoEmail] = useState<QuotationRow | null>(null);   // quotation → email dialog (same as edit page)
  const [quoThread, setQuoThread] = useState<QuotationRow | null>(null); // quotation email thread
  const [slipQuo, setSlipQuo] = useState<{ id: string; docNo?: string; clientName?: string } | null>(null); // client payment slips
  const [dupFrom, setDupFrom] = useState<string | null>(null);   // duplicate source → open a pre-filled NEW quotation
  // Quotations whose note pop-down is open — the plus after the eye (Calem, Bugify #20cc8313). A saved note
  // goes into its row where it stands, no reload; a first note closes the pop-down, as a job's first comment
  // does on Active Jobs, and after an edit or a delete it stays open for the next change.
  const [cmtOpen, setCmtOpen] = useState<Set<string>>(new Set());
  const setCmt = (id: string, open: boolean) => setCmtOpen(p => { const n = new Set(p); open ? n.add(id) : n.delete(id); return n; });
  const commentChanged = (id: string, comment: string, bullets?: string[]) => {
    setRows(rs => rs.map(x => (x.id === id ? { ...x, comment } : x)));
    if (bullets) setCmt(id, false);
  };

  const { badgeOnly, clearBadge } = useBadgeOnly();
  const [isApprover, setIsApprover] = useState(false);
  const [pageSize, setPageSize] = useState(getPageSize());
  const creatorCol = useCreatorCol();   // Created By, hidden unless management opens it (report d7c20856)
  // Years folded away, and the ones the operator has opened. Session-only on purpose: every visit
  // starts with the old years put away, which is the point of the fold (owner 2026-09-19).
  const [folded, setFolded] = useState<{ year: string; n: number }[]>([]);
  const [openYears, setOpenYears] = useState<string[]>([]);
  const toggleYear = (y: string) => setOpenYears(prev => prev.includes(y) ? prev.filter(x => x !== y) : [...prev, y]);
  const load = async (p = page, comp = company, st = status) => {
    setLoading(true); setErr('');
    try {
      const res = await api.getQuotations({ page: p, company: comp, status: st, from, to, badge: badgeOnly || undefined, limit: pageSize, years: openYears.join(',') });
      setIsApprover(!!(res as any).isApprover);
      setRows(res.rows); setTotal(res.total); setPage(res.page); setFirms(res.companies || []); setFolded((res as any).folded || []);
    } catch (e: any) { setErr(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(1, company, status); }, [company, status, from, to, badgeOnly, pageSize, openYears]); // eslint-disable-line

  // Delete a  quotation — blocked when it has receipt(s); always double-confirmed.
  const delQuotation = async (r: QuotationRow, e: React.MouseEvent) => {
    e.stopPropagation();
    if ((r.receipts || []).length) { window.alert(t('pk_del_blocked', { no: r.doc_no })); return; }
    if (!window.confirm(t('pk_del_confirm', { no: r.doc_no }))) return;
    if (!window.confirm(t('pk_del_confirm2', { no: r.doc_no }))) return;
    try { await api.deleteQuotation(r.id); load(); } catch (e2: any) { window.alert(e2.message); }
  };
  useEffect(() => { if (initialOpen) setFormId(initialOpen); }, [initialOpen]);

  // Quotation → Invoice. The impact call surfaces the two things worth a pause BEFORE creating a
  // second financial document: this quote was already invoiced, and/or money already received here
  // stays on the quotation's receipts (the new invoice starts unpaid).
  const [invBusy, setInvBusy] = useState<string | null>(null);
  const issueInvoice = async (r: QuotationRow) => {
    setInvBusy(r.id);
    try {
      const impact: any = await api.invoiceFromQuotationImpact(r.id);
      const warn: string[] = [];
      if (impact.existing?.length) warn.push(t('qo_inv_exists', { list: impact.existing.join(', ') }));
      if (impact.receipts?.length) warn.push(t('qo_inv_has_receipts', { n: String(impact.receipts.length) }));
      const msg = t('qo_inv_confirm', { no: r.doc_no }) + (warn.length ? `\n\n${warn.join('\n\n')}` : '');
      if (!window.confirm(msg)) return;
      const res: any = await api.invoiceFromQuotation(r.id);
      alert(t('qo_inv_done', { no: res.doc_no }));
    } catch (e: any) { alert(e.message); }
    finally { setInvBusy(null); }
  };

  const [jobBusy, setJobBusy] = useState<string | null>(null);
  const genJobs = async (r: QuotationRow) => {
    if (!window.confirm(t('qo_gen_jobs_confirm', { no: r.doc_no }))) return;
    setJobBusy(r.id);
    try { const res: any = await api.generateQuotationJobs(r.id); alert(t('qo_gen_jobs_done', { n: res.jobs })); }
    catch (e: any) { alert(e.message); } finally { setJobBusy(null); }
  };

  const filtered = rows.filter(r => !q.trim() || [r.doc_no, r.client_name].join(' ').toLowerCase().includes(q.trim().toLowerCase()));
  // 11 → 12px (Calem, report 66c9d174 — see the Invoice list's th); black, regular weight, the darker line (report 4bd4ae4e).
  const th: React.CSSProperties = { textAlign: 'left', fontSize: 12, fontWeight: 400, color: 'var(--text-primary)', padding: '10px 12px', lineHeight: '16px', whiteSpace: 'nowrap', borderBottom: '1px solid var(--border-strong)', position: 'sticky', top: 0, background: 'var(--bg-surface)' };   // the page's white (report ddf1ba0e — see thEdge)
  const td: React.CSSProperties = { fontSize: 13, color: 'var(--text-primary)', padding: '10px 12px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' };
  // A plain row is the top menu's blue, one shade lighter (Calem, Bugify #1a85b4b0 and #37d916aa — see rowTint), not
  // transparent. An expired quotation's pink (expiredBg) and a receipt's line under its quotation keep their own colours.
  const plainBg = rowTint('quotation', 1)!;
  // An expired quotation's row: past its Valid Until and not paid, the server's 'expired' (Calem 2026-09-28, Bugify
  // #d14e02b5: "show a red background only for those whose date is past the expiry date of the quote and make the red
  // background color one shade lighter"). The pink wash the row had, rgba(236,72,153,0.12), one shade lighter: #FDEFF6,
  // laid on white like plainBg, so solid. It used to go on any quotation unpaid for more than 7 days since issue — the
  // Invoice list's rule, which keeps it — so a quote still well inside its validity turned pink a week after issue.
  const expiredBg = lighten('rgba(236,72,153,0.12)', 1);
  // Created By has a width of its own (Calem, Bugify #43e33304 reopened: "remove 35% of the Created By
  // column and give that space back to the Client column"). It had none, so it was as wide as its longest
  // name — 'SLF Legal Office' at 12px plus the 12px padding a side, about 130 — and 85 is about 65% of
  // that, roughly its own "Created by" heading (estimated from the font, not measured on screen). A longer
  // name ends in "…" and shows whole on hover rather than pushing the column back out; the cell takes the
  // name's 12px grey so the "…" matches it. Client still has no width, so it is free to take the room.
  const creatorW = 85;
  const cols = creatorCol.show ? 11 : 10;   // a gap row spans them all (CardGap)
  // In the top menu's blue, as on Invoice (Calem, Bugify #667b55cb — see menuPill). Just the name, no unpaid / expired
  // counts after it, as on Invoice (report 38417c66).
  const tab = (key: string, label: string) => (
    <button key={key} onClick={() => setCompany(key)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 999, border: '1px solid', fontSize: 12, fontWeight: 600, cursor: 'pointer', ...menuPill('quotation', company === key) }}>{label}</button>
  );

  // The page in white, as on Invoice (Calem, report ddf1ba0e, 3rd opening — see cardLine).
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, background: 'var(--bg-surface)' }}>
      {badgeOnly && <BadgeFilterBar count={total} labelKey="bf_approvals" onClear={clearBadge} />}
      <PeakToolbar
        filters={<>
          {tab('all', t('pk_all'))}
          {firms.map(f => tab(f.id, firmLabel(f)))}
          <select value={status} onChange={e => setStatus(e.target.value)} style={{ padding: '6px 10px', fontSize: 12, borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--bg-app)', color: 'var(--text-primary)', marginLeft: 4 }}>
            <option value="all">{t('pk_all_status')}</option>
            <option value="unpaid">{t('pk_st_unpaid')}</option>
            {['draft', 'partial', 'paid', 'expired'].map(s => <option key={s} value={s}>{t(STATUS[s].label)}</option>)}
          </select>
        </>}
        right={<>
          <DateRange k="quotation" from={from} to={to} setFrom={setFrom} setTo={setTo} />
          {/* A blue bubble like the pills, as on Invoice (Calem, Bugify #cba01676, 6th opening — see there). */}
          <div style={{ position: 'relative' }}>
            <i className="ti ti-search" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: tabColor('quotation') }} aria-hidden />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('pk_search')} className="mf-ph-inherit" style={{ width: 200, padding: '6px 14px 6px 32px', fontSize: 12, fontWeight: 600, borderRadius: 999, border: '1px solid', outline: 'none', ...menuPill('quotation', false) }} />
          </div>
          <button onClick={() => load()} title={t('refresh')} style={{ width: 32, height: 32, borderRadius: 'var(--radius-md)', border: 'none', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}><i className="ti ti-refresh" style={{ fontSize: 15 }} aria-hidden /></button>
          <CreatorColButton col={creatorCol} />
        </>}
      />
      {/* The page's white, as on the Jobs lists: between the cards, down either side of them and on under the last
          one (see cardLine). */}
      <div style={{ flex: 1, overflow: 'auto', background: 'var(--bg-surface)' }}>
        {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '10px 16px' }}>{err}</div>}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><i className="ti ti-loader" style={{ fontSize: 24, color: 'var(--text-tertiary)' }} aria-hidden /></div>
        ) : (
          // Separate borders with no spacing, as on the Jobs and Tracking lists: collapsed borders can't be rounded.
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, padding: `0 ${CARD_INSET}px` }}>
            <thead><tr>
              <th style={th}><span aria-hidden style={{ ...thEdge, left: -CARD_INSET }} />{t('pk_doc_no')}</th><th style={th}>{t('pk_client')}</th>
              <th style={th}>{t('qo_description')}</th>
              <th style={th}>{t('pk_issue')}</th><th style={th}>{t('qo_valid_until')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('pk_amount')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('pk_paid')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('pk_balance')}</th>
              <th style={th}>{t('pk_status')}</th>
              {/* 85 wide (Calem, Bugify #43e33304 reopened — see creatorW); hidden unless management opens it (report d7c20856). */}
              {creatorCol.show && <th style={{ ...th, width: creatorW }}>{t('pk_created_by')}</th>}
              <th style={{ ...th, width: ACT_COL_W }}><span aria-hidden style={{ ...thEdge, right: -CARD_INSET }} /></th>
            </tr></thead>
            <tbody>
              {filtered.map((r, i) => {
                const s = STATUS[r.status] || STATUS.draft;
                // Pink only once expired, see expiredBg.
                const rowBg = r.status === 'expired' ? expiredBg : plainBg;
                // The quotation's card (see cardEdge): its row opens it, and closes it too unless receipts follow.
                const { cell, edgeL, edgeR } = cardEdge(td, true, !(r.receipts || []).length);
                return (
                  <React.Fragment key={r.id}>
                  <CardGap cols={cols} h={i ? CARD_GAP : HEAD_GAP} />
                  <tr style={{ cursor: 'pointer', background: rowBg }} onClick={() => setFormId(r.id)}
                    onMouseEnter={e => ringRow(e.currentTarget, true)}
                    onMouseLeave={e => ringRow(e.currentTarget, false)}>
                    <td style={{ ...cell, ...edgeL, color: docNoColor, fontWeight: 600 }}><DocNoLink no={r.doc_no} onOpen={() => setFormId(r.id)} />
                      {/* A quotation awaiting info is not up for approval yet — its awaiting symbol is in the Client cell. */}
                      {!Number((r as any).awaiting_info) && !!r.approval_required && !r.approval_ok && <ApprovalBadge title={t('qo_appr_hint')} notifiedAt={Number((r as any).approval_notified_at) || 0} notifiedTo={(r as any).approval_notified_to} />}</td>
                    <td style={cell}>
                      {!!Number((r as any).awaiting_info) && <AwaitingBadge note={(r as any).awaiting_note} />}
                      <OpenedBadge at={r.opened_at} />
                      {/* The internal note's plus, after the eye and before the name (Calem, Bugify #20cc8313): Active
                          Jobs' comment plus in the Quotation menu's blue on its pale blue once there is a note, the
                          light grey outline until then (see CommentPlus in Jobs). Staff-only — not the printed Remark. */}
                      <CommentPlus comments={r.comment || ''} color={TAB_GROUPS.peak.color} wash={TAB_GROUPS.peak.tint} open={cmtOpen.has(r.id)}
                        onToggle={() => setCmt(r.id, !cmtOpen.has(r.id))} t={t} addTip="qo_cmt_add_tip" hasTip="qo_cmt_has_tip" />
                      {displayClientName(r.client_name, r.client_first, r.client_last) || '—'}
                      <ConfirmBadge done={r.confirm_done} total={r.confirm_total} accepted={!!r.accepted_at} />
                      {/* Straight after the 0/5 badge; in the last column it stretched the ✉/⋮ (Calem, Bugify #708bbb27 reopened).
                          Light fill + dark border and text, the hourglass circle's old blue — was solid #1D4ED8 with white text (Calem,
                          Bugify #43e33304 3rd opening: "dark border and lighter background, same as the page theme"). */}
                      {!!Number((r as any).awaiting_info) && (
                        <button onClick={async e => {
                            e.stopPropagation();
                            const who = /calem/i.test(String((r as any).created_by_label || '')) ? 'Nick' : 'Calem';
                            if (!window.confirm(t('qo_await_confirm', { name: who }))) return;
                            try { await api.quotationAwaiting(r.id, false); load(); } catch (e2: any) { window.alert(e2.message || 'Failed'); }
                          }} title={(r as any).awaiting_note || ''}
                          style={{ marginLeft: 7, verticalAlign: 'middle', display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 8, border: '1px solid #1D4ED8', background: '#EFF6FF', color: '#1D4ED8', fontWeight: 800, fontSize: 11.5, cursor: 'pointer' }}>
                          {t('qo_await_done')}
                        </button>
                      )}
                      {/* Payment slips the client uploaded on their quotation link (2026-09-18). */}
                      <SlipBadge count={Number((r as any).slip_count) || 0} tbc={Number((r as any).slip_tbc) || 0}
                        onClick={() => setSlipQuo({ id: r.id, docNo: r.doc_no, clientName: r.client_name })} />
                      {/* The plus's pop-down, under the name and its badges, as on Active Jobs: the note with its pencil
                          and trash, or the one box a quotation without a note opens into. The cell keeps its one line
                          unwrapped; the note wraps. Clicks in it don't open the quotation. */}
                      {cmtOpen.has(r.id) && (
                        <div style={{ whiteSpace: 'normal', marginTop: 6, cursor: 'auto' }}>
                          <CommentPanel comments={r.comment || ''} t={t} onClose={() => setCmt(r.id, false)} onChanged={(c, b) => commentChanged(r.id, c, b)}
                            load={() => api.getQuotationComment(r.id)} store={value => api.setQuotationComment(r.id, value)} />
                        </div>
                      )}
                    </td>
                    <td style={{ ...cell, color: 'var(--text-secondary)', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={(r as any).description || ''}>{(r as any).description || '—'}</td>
                    <td style={{ ...cell, color: 'var(--text-secondary)' }}>{fmtDate(r.issue_date)}</td>
                    <td style={{ ...cell, color: r.status === 'expired' ? '#E24B4A' : 'var(--text-secondary)' }}>{fmtDate(r.valid_until)}</td>
                    <td style={{ ...cell, textAlign: 'right', fontWeight: 600 }}>{fmtAmt(r.total_amount || r.net_to_pay)}</td>
                    <td style={{ ...cell, textAlign: 'right', color: grossPaid(r) > 0 ? '#1D9E75' : 'var(--text-tertiary)' }}>{grossPaid(r) > 0 ? fmtAmt(grossPaid(r)) : '—'}</td>
                    <td style={{ ...cell, textAlign: 'right', fontWeight: 600, color: r.balance > 0 ? '#D97706' : 'var(--text-tertiary)' }}>{r.balance > 0 ? fmtAmt(r.balance) : '—'}</td>
                    <td style={cell}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, fontWeight: 600, color: s.color }}><i className={`ti ti-${s.dot}`} style={{ fontSize: 12 }} aria-hidden />{t(s.label)}</span>
                    </td>
                    {creatorCol.show && <td style={{ ...cell, fontSize: 12, color: 'var(--text-secondary)', width: creatorW, maxWidth: creatorW, overflow: 'hidden', textOverflow: 'ellipsis' }} title={(r as any).created_by_label || ''}><Creator label={(r as any).created_by_label} /></td>}
                    <td style={{ ...cell, ...edgeR }} onClick={e => e.stopPropagation()}>
                      {Number((r as any).awaiting_info) ? (
                        <ActionCell
                          mail={<SentBadge count={(r as any).email_thread_count ?? r.email_sent_count} last={r.email_last_sent_at} onClick={() => setQuoThread(r)} />}
                        />
                      ) : !!r.approval_required && !r.approval_ok ? (
                        // Approval is Calem's and Nick's alone (owner 2026-08-31): only they see the
                        // button, and it OPENS the approval page directly instead of copying a link.
                        <ActionCell
                          mail={<SentBadge count={(r as any).email_thread_count ?? r.email_sent_count} last={r.email_last_sent_at} onClick={() => setQuoThread(r)} />}
                          mid={isApprover && (
                            <button onClick={async () => {
                              try { const lk: any = await api.quotationApprovalLink(r.id); window.open(lk.url, '_blank'); }
                              catch (e: any) { window.alert(e.message || 'Failed'); }
                            }} title={t('qo_appr_open')}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 8, border: 'none', background: '#B45309', color: '#fff', fontWeight: 800, fontSize: 11.5, cursor: 'pointer' }}>
                              <i className="ti ti-external-link" style={{ fontSize: 12 }} />{t('qo_appr_open')}
                            </button>
                          )}
                        />
                      ) : (
                        <ActionCell
                          mail={<SentBadge count={(r as any).email_thread_count ?? r.email_sent_count} last={r.email_last_sent_at} onClick={() => setQuoThread(r)} />}
                          menu={<RowMenu title={t('pk_actions')} actions={[
                            { label: t('iv_email'), icon: 'send', onClick: () => setQuoEmail(r) },
                            { label: t('qo_copy'), icon: 'copy', onClick: () => { setDupFrom(r.id); setFormId(null); } },
                            { label: t('qo_gen_jobs'), icon: 'briefcase', busy: jobBusy === r.id, onClick: () => genJobs(r) },
                            { label: t('qo_issue_invoice'), icon: 'file-invoice', busy: invBusy === r.id, onClick: () => issueInvoice(r) },
                            ...(r.status !== 'paid' ? [{ label: t('rc_issue'), icon: 'receipt', onClick: () => setReceiptFor(r) }] : []),
                            ...(!(r.receipts || []).length ? [{ label: t('delete_short'), icon: 'trash', danger: true, onClick: () => delQuotation(r, { stopPropagation: () => {} } as any) }] : []),
                          ]} />}
                        />
                      )}
                    </td>
                  </tr>
                  {(r.receipts || []).map((rc, j, all) => {
                    // Inside the quotation's card; the last receipt closes it.
                    const k = cardEdge(td, false, j === all.length - 1);
                    return (
                    <tr key={rc.id} style={{ background: '#F7FAFB' }}>
                      <td style={{ ...k.cell, ...k.edgeL, paddingLeft: 28, color: 'var(--text-tertiary)' }} colSpan={5}>
                        <i className="ti ti-corner-down-right" style={{ marginRight: 6 }} aria-hidden />
                        <DocNoLink no={rc.doc_no} id={rc.id} kind="receipt" style={{ fontWeight: 600 }} />
                        <span style={{ marginLeft: 10 }}>{fmtDate(rc.payment_date || rc.issue_date)}</span>
                      </td>
                      <td style={{ ...k.cell, textAlign: 'right', color: '#1D9E75', fontWeight: 600 }}>{fmtAmt(grossRc(rc))}</td>
                      {/* Under Paid · Balance · Status, and Created By when it shows. */}
                      <td colSpan={creatorCol.show ? 4 : 3} style={{ ...k.cell, color: 'var(--text-tertiary)', fontSize: 11 }}>{t('rc_receipt')}</td>
                      <td style={{ ...k.cell, ...k.edgeR }}>
                        <ActionCell
                          mail={<SentBadge count={rc.email_sent_count} last={rc.email_last_sent_at} onClick={() => setRcThread({ id: rc.id, doc_no: rc.doc_no, client_name: r.client_name })} />}
                          menu={<RowMenu title={t('pk_actions')} actions={[
                            { label: t('iv_email'), icon: 'send', onClick: () => setRcEmail({ id: rc.id, doc_no: rc.doc_no, client_name: r.client_name }) },
                            { label: 'PDF', icon: 'file-type-pdf', onClick: () => { api.openReceiptPdf(rc.id).catch(() => {}); } },
                            { label: t('delete_short'), icon: 'trash', danger: true, onClick: async () => { await deleteReceiptFlow(rc.id, load); } },
                          ]} />}
                        />
                      </td>
                    </tr>
                    );
                  })}
                  </React.Fragment>
                );
              })}
              {filtered.length > 0 && <CardGap cols={cols} />}
              {filtered.length === 0 && (
                <tr><td style={{ ...td, textAlign: 'center', color: 'var(--text-tertiary)', padding: 48 }} colSpan={creatorCol.show ? 11 : 10}>{t('pk_empty')}</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      <YearFolds folded={folded} open={openYears} onToggle={toggleYear} />
      <Pagination page={page} total={total} limit={pageSize} onPage={p => load(p)} onLimit={n => setPageSize(n)} />
      <Fab onClick={() => { setDupFrom(null); setFormId(null); }} title={t('qo_new')} color={tabColor('quotation')} />
      {formId !== false && <InvoiceForm invoiceId={formId} kind="quotation" duplicateFrom={formId === null ? dupFrom : undefined} onClose={() => { setFormId(false); setDupFrom(null); }} onSaved={() => load()} />}
      {receiptFor && <ReceiptForm seed={{ parentType: 'quotation', parent: receiptFor }} onClose={() => setReceiptFor(null)} onSaved={() => load()} />}
      {rcEmail && <ReceiptEmailComposer receiptId={rcEmail.id} defaultName={rcEmail.client_name} onClose={() => setRcEmail(null)} onSent={() => load()} />}
      {rcThread && <ReceiptEmailThread receiptId={rcThread.id} docNo={rcThread.doc_no} clientName={rcThread.client_name} onClose={() => setRcThread(null)} />}
      {quoEmail && <QuotationEmailComposer quotationId={quoEmail.id} defaultName={quoEmail.client_name} onClose={() => setQuoEmail(null)} onSent={() => load()} />}
      {quoThread && <QuotationEmailThread quotationId={quoThread.id} docNo={quoThread.doc_no} clientName={quoThread.client_name} onClose={() => setQuoThread(null)} />}
      {slipQuo && <QuoSlipViewer quotationId={slipQuo.id} docNo={slipQuo.docNo} clientName={slipQuo.clientName} onClose={() => setSlipQuo(null)} />}
    </div>
  );
}

// ── Receipt list (flat, all receipts) ───────────────────────────────────────
export function ReceiptList({ initialOpen }: { initialOpen?: string } = {}) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<ReceiptRow[]>([]);
  const [firms, setFirms] = useState<Firm[]>([]);
  const [counts, setCounts] = useState<Record<string, { unpaid: number; expired: number }>>({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [company, setCompany] = useState('all');
  const [q, setQ] = useState('');
  const dr = defaultRange();
  const [from, setFrom] = useState(dr.from);
  const [to, setTo] = useState(dr.to);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [formId, setFormId] = useState<string | null | false>(false); // false=closed, null=new, string=edit
  const [threadFor, setThreadFor] = useState<ReceiptRow | null>(null);   // email-thread viewer target
  const [emailFor, setEmailFor] = useState<ReceiptRow | null>(null);     // quick send-email composer target
  const [showDel, setShowDel] = useState(false);   // deleted receipts hidden unless asked for

  const { badgeOnly, clearBadge } = useBadgeOnly();
  const [isApprover, setIsApprover] = useState(false);
  const [pageSize, setPageSize] = useState(getPageSize());
  const creatorCol = useCreatorCol();   // Created By, hidden unless management opens it (report d7c20856)
  // Years folded away, and the ones the operator has opened. Session-only on purpose: every visit
  // starts with the old years put away, which is the point of the fold (owner 2026-09-19).
  const [folded, setFolded] = useState<{ year: string; n: number }[]>([]);
  const [openYears, setOpenYears] = useState<string[]>([]);
  const toggleYear = (y: string) => setOpenYears(prev => prev.includes(y) ? prev.filter(x => x !== y) : [...prev, y]);
  const load = async (p = page, comp = company) => {
    setLoading(true); setErr('');
    try { const res = await api.getReceipts({ page: p, company: comp, from, to, showDeleted: showDel || undefined, badge: badgeOnly || undefined, limit: pageSize, years: openYears.join(',') }); setIsApprover(!!(res as any).isApprover); setRows(res.rows); setTotal(res.total); setPage(res.page); setFirms(res.companies || []); setCounts((res as any).counts || {}); setFolded((res as any).folded || []); }
    catch (e: any) { setErr(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(1, company); }, [company, from, to, showDel, badgeOnly, pageSize, openYears]); // eslint-disable-line
  // Deep-link (?tab=receipt&open=…) — used by the approval page's "open & edit in the system".
  useEffect(() => { if (initialOpen) setFormId(initialOpen); }, [initialOpen]);

  const filtered = rows.filter(r => !q.trim() || [r.doc_no, r.client_name, r.invoice_no, r.quotation_no].join(' ').toLowerCase().includes(q.trim().toLowerCase()));
  // 11 → 12px (Calem, report 66c9d174 — see the Invoice list's th); black, regular weight, the darker line (report 4bd4ae4e).
  const th: React.CSSProperties = { textAlign: 'left', fontSize: 12, fontWeight: 400, color: 'var(--text-primary)', padding: '10px 12px', lineHeight: '16px', whiteSpace: 'nowrap', borderBottom: '1px solid var(--border-strong)', position: 'sticky', top: 0, background: 'var(--bg-surface)' };   // the page's white (report ddf1ba0e — see thEdge)
  const td: React.CSSProperties = { fontSize: 13, color: 'var(--text-primary)', padding: '10px 12px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' };
  // A receipt's card is the top menu's blue, one shade lighter (Calem, Bugify #1a85b4b0 and #37d916aa — see rowTint), set
  // on its row as the Invoice and Quotation lists set theirs (Bugify #6eb5a0b8 — see cardLine). It was on the table body,
  // the rows see-through so the list's zebra stripes showed on every other one; there it would fill the grey between the
  // cards too, and every receipt is a card of its own now, so there are no stripes.
  const plainBg = rowTint('receipt', 1)!;
  // One receipt, one card: its row is the card's first and last (see cardEdge).
  const { cell, edgeL, edgeR } = cardEdge(td, true, true);
  const cols = creatorCol.show ? 7 : 6;   // a gap row spans them all (CardGap)
  // In the top menu's blue, as on Invoice (Calem, Bugify #667b55cb — see menuPill).
  const tab = (key: string, label: string) => (
    <button key={key} onClick={() => setCompany(key)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 999, border: '1px solid', fontSize: 12, fontWeight: 600, cursor: 'pointer', ...menuPill('receipt', company === key) }}>{label}<TabCounts c={counts[key]} t={t} /></button>
  );

  // The page in white, as on Invoice (Calem, report ddf1ba0e, 3rd opening — see cardLine).
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, background: 'var(--bg-surface)' }}>
      {badgeOnly && <BadgeFilterBar count={total} labelKey="bf_approvals" onClear={clearBadge} />}
      <PeakToolbar
        filters={<>
          {tab('all', t('pk_all'))}{firms.map(f => tab(f.id, firmLabel(f)))}
          <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--text-secondary)', cursor: 'pointer', marginLeft: 6, whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={showDel} onChange={e => setShowDel(e.target.checked)} style={{ cursor: 'pointer' }} />
            {t('rc_show_deleted')}
          </label>
        </>}
        right={<>
          <DateRange k="receipt" from={from} to={to} setFrom={setFrom} setTo={setTo} />
          {/* A blue bubble like the pills, as on Invoice (Calem, Bugify #cba01676, 6th opening — see there), with
              Invoice's magnifier; it had none, its words 10px in. */}
          <div style={{ position: 'relative' }}>
            <i className="ti ti-search" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: tabColor('receipt') }} aria-hidden />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('pk_search')} className="mf-ph-inherit" style={{ width: 200, padding: '6px 14px 6px 32px', fontSize: 12, fontWeight: 600, borderRadius: 999, border: '1px solid', outline: 'none', ...menuPill('receipt', false) }} />
          </div>
          <button onClick={() => load()} title={t('refresh')} style={{ width: 32, height: 32, borderRadius: 'var(--radius-md)', border: 'none', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}><i className="ti ti-refresh" style={{ fontSize: 15 }} aria-hidden /></button>
          <CreatorColButton col={creatorCol} />
        </>}
      />
      {/* The page's white, as on the Jobs lists: between the cards, down either side of them and on under the last
          one (see cardLine). */}
      <div style={{ flex: 1, overflow: 'auto', background: 'var(--bg-surface)' }}>
        {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '10px 16px' }}>{err}</div>}
        {loading ? <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><i className="ti ti-loader" style={{ fontSize: 24, color: 'var(--text-tertiary)' }} aria-hidden /></div> : (
          // Separate borders with no spacing, as on the Jobs and Tracking lists: collapsed borders can't be rounded.
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, padding: `0 ${CARD_INSET}px` }}>
            <thead><tr>
              <th style={th}><span aria-hidden style={{ ...thEdge, left: -CARD_INSET }} />{t('pk_doc_no')}</th><th style={th}>{t('rc_for_doc')}</th><th style={th}>{t('pk_client')}</th>
              <th style={th}>{t('rc_pay_date')}</th><th style={{ ...th, textAlign: 'right' }}>{t('rc_amount')}</th>{creatorCol.show && <th style={th}>{t('pk_created_by')}</th>}<th style={{ ...th, width: ACT_COL_W }}><span aria-hidden style={{ ...thEdge, right: -CARD_INSET }} /></th>
            </tr></thead>
            <tbody>
              {filtered.map((r, i) => {
                // Deleted receipts stay VISIBLE — greyed, struck-through, "Deleted" badge, no actions,
                // and not clickable (its number is permanently retired, never reused).
                // A card like the rest, told apart as before by the row's opacity alone — which now fades the card's
                // blue and its edge with it, as they are on the row — and with no hover ring, as it opens nothing.
                const isDel = !!r.deleted;
                return (
                <React.Fragment key={r.id}>
                <CardGap cols={cols} h={i ? CARD_GAP : HEAD_GAP} />
                <tr style={{ cursor: isDel ? 'default' : 'pointer', background: plainBg, opacity: isDel ? 0.55 : 1 }} onClick={isDel ? undefined : () => setFormId(r.id)}
                  onMouseEnter={e => { if (!isDel) ringRow(e.currentTarget, true); }} onMouseLeave={e => ringRow(e.currentTarget, false)}>
                  <td style={{ ...cell, ...edgeL, color: isDel ? 'var(--text-tertiary)' : docNoColor, fontWeight: 600 }}>
                    <span style={{ textDecoration: isDel ? 'line-through' : 'none' }}>{isDel ? r.doc_no : <DocNoLink no={r.doc_no} onOpen={() => setFormId(r.id)} />}</span>
                    {isDel && <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, color: '#B42318', background: '#FCEBEB', padding: '1px 6px', borderRadius: 4, textTransform: 'uppercase', letterSpacing: 0.3 }}>{t('rc_deleted')}</span>}
                    {!isDel && (!!(r as any).pending_approval || (!!(r as any).approval_required && !(r as any).approval_ok)) && <ApprovalBadge notifiedAt={Number((r as any).approval_notified_at) || 0} notifiedTo={(r as any).approval_notified_to} />}
                    {/* Issued before the bank credit was visible — clears when the payment is
                        matched & linked in Bank (owner 2026-08-18). */}
                    {!isDel && !!r.pending_bank && <span title="No bank credit linked yet — match the incoming payment in Bank · Accounting/Legal and choose &quot;Link to existing receipt&quot;." style={{ marginLeft: 8, fontSize: 10, fontWeight: 800, color: '#92400E', background: '#FFFBEB', border: '1px solid #F1C232', padding: '1px 6px', borderRadius: 4, textTransform: 'uppercase', letterSpacing: 0.3 }}>PENDING BANK</span>}
                  </td>
                  <td style={{ ...cell, color: isDel ? 'var(--text-tertiary)' : docNoColor }}>{isDel ? (r.source_no || r.invoice_no || r.quotation_no || '—') : <DocNoLink no={r.source_no || r.invoice_no || r.quotation_no || '—'} />}</td>
                  <td style={cell}>
                    {displayClientName(r.client_name, r.client_first, r.client_last) || '—'}
                    {/* Why it went, and who removed it — an audited deletion whose reason is buried
                        in a table nobody opens is not much of an audit trail. */}
                    {isDel && (r.deleted_reason || r.deleted_by_name) && (
                      <div style={{ fontSize: 10.5, color: '#B42318', marginTop: 2, whiteSpace: 'normal' }}>
                        {r.deleted_reason || '—'}
                        {r.deleted_by_name ? ` · ${t('rc_deleted_by')} ${r.deleted_by_name}` : ''}
                        {r.deleted_at ? ` · ${fmtDate(new Date(r.deleted_at * 1000).toISOString().slice(0, 10))}` : ''}
                      </div>
                    )}
                  </td>
                  <td style={{ ...cell, color: 'var(--text-secondary)' }}>{fmtDate(r.payment_date || r.issue_date)}</td>
                  <td style={{ ...cell, textAlign: 'right', fontWeight: 600, color: isDel ? 'var(--text-tertiary)' : '#1D9E75' }}>{fmtAmt(grossRc(r))}</td>
                  {creatorCol.show && <td style={cell}><Creator label={(r as any).created_by_label} /></td>}
                  <td style={{ ...cell, ...edgeR }}>
                    {!isDel && (
                      <ActionCell
                        mail={<SentBadge count={(r as any).email_thread_count ?? r.email_sent_count} last={r.email_last_sent_at} onClick={() => setThreadFor(r)} />}
                        mid={isApprover && !!(r as any).approval_required && !(r as any).approval_ok && (
                          <button onClick={async e => { e.stopPropagation(); try { const lk: any = await api.receiptApprovalLink(r.id); window.open(lk.url, '_blank'); } catch (e2: any) { window.alert(e2.message || 'Failed'); } }}
                            title={t('qo_appr_open')} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 8, border: 'none', background: '#B45309', color: '#fff', fontWeight: 800, fontSize: 11.5, cursor: 'pointer' }}>
                            <i className="ti ti-external-link" style={{ fontSize: 12 }} />{t('qo_appr_open')}
                          </button>
                        )}
                        menu={<RowMenu title={t('pk_actions')} actions={[
                          { label: t('iv_email'), icon: 'send', onClick: () => setEmailFor(r) },
                          { label: 'PDF', icon: 'file-type-pdf', onClick: () => { api.openReceiptPdf(r.id).catch(() => {}); } },
                          { label: t('delete_short'), icon: 'trash', danger: true, onClick: async () => { await deleteReceiptFlow(r.id, load); } },
                        ]} />}
                      />
                    )}
                  </td>
                </tr>
                </React.Fragment>
                );
              })}
              {filtered.length > 0 && <CardGap cols={cols} />}
              {filtered.length === 0 && <tr><td style={{ ...td, textAlign: 'center', color: 'var(--text-tertiary)', padding: 48 }} colSpan={creatorCol.show ? 7 : 6}>{t('rc_empty')}</td></tr>}
            </tbody>
          </table>
        )}
      </div>
      <YearFolds folded={folded} open={openYears} onToggle={toggleYear} />
      <Pagination page={page} total={total} limit={pageSize} onPage={p => load(p)} onLimit={n => setPageSize(n)} />
      <Fab onClick={() => setFormId(null)} title={t('rc_new')} color={tabColor('receipt')} />
      {formId !== false && <ReceiptForm receiptId={formId} onClose={() => setFormId(false)} onSaved={() => load()} />}
      {threadFor && <ReceiptEmailThread receiptId={threadFor.id} docNo={threadFor.doc_no} clientName={threadFor.client_name} onClose={() => setThreadFor(null)} />}
      {emailFor && <ReceiptEmailComposer receiptId={emailFor.id} defaultName={emailFor.client_name} onClose={() => setEmailFor(null)} onSent={() => load()} />}
    </div>
  );
}

function Placeholder({ label }: { label: string }) {
  const { t } = useTranslation();
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, color: 'var(--text-tertiary)' }}>
      <div style={{ width: 64, height: 64, borderRadius: 18, background: 'var(--bg-active)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)' }}><i className="ti ti-tools" style={{ fontSize: 28 }} aria-hidden /></div>
      <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>{label}</div>
      <div style={{ fontSize: 13 }}>{t('under_development')}</div>
    </div>
  );
}

// ── Reusable Peak view (standalone page + dashboard tab) ─────────────────────
export function PeakView({ header }: { header?: React.ReactNode } = {}) {
  const { t } = useTranslation();
  const { me } = useStore();
  const [params, setParams] = useSearchParams();
  const paramTab = params.get('tab');
  const openId = params.get('open') || undefined;   // deep-link: open this doc's form
  // The Thai-business "six-pack": Contact + Services + Quotation + Invoice + Receipt + Bookkeeping,
  // all in one place. The three folded-in modules keep their own permission keys.
  const canExtra = (k: 'contact' | 'services' | 'bookkeeping') => me?.role === 'admin' || (me?.modules || []).includes(k);
  const validTabs = new Set<PeakTab>(['invoice', 'quotation', 'receipt', ...(['contact', 'services', 'bookkeeping'] as const).filter(canExtra)]);
  const [tab, setTab] = useState<PeakTab>(validTabs.has(paramTab as PeakTab) ? paramTab as PeakTab : 'invoice');
  // A refresh stays where you are (owner 2026-09-25). The tab was READ from ?tab= on mount but
  // never written back, so reloading anywhere in Peak landed on Invoice — and the Bookkeeping tab,
  // which does put its own client in the URL, reloaded into a different tab entirely. Same shape the
  // dashboard (Portal) and the Tax module already use, and replace rather than push, so Back still
  // leaves the page instead of walking backwards through the tabs one at a time.
  const selectTab = (k: PeakTab) => {
    setTab(k);
    setParams(p => { const n = new URLSearchParams(p); n.set('tab', k); return n; }, { replace: true });
  };
  const tabBtn = (k: PeakTab, label: string) => (
    <button key={k} onClick={() => selectTab(k)} style={{ padding: '8px 18px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', border: 'none', background: 'transparent', color: tab === k ? 'var(--accent)' : 'var(--text-tertiary)', borderBottom: `3px solid ${tab === k ? 'var(--accent)' : 'transparent'}` }}>{label}</button>
  );
  // The title and the tabs share ONE line (owner 2026-09-25): the page name sat on a row of its own
  // above the tabs, which cost ~45px of every screen for one short phrase. `header` is that title
  // block, handed in by whichever page is hosting the view; without it the tabs stand alone as before.
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, background: 'var(--bg-app)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: header ? 12 : 4, padding: header ? '6px 16px 0' : '0 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
        {header}
        {/* Scrolls rather than wraps on a narrow window, so the tabs never push the title off the line. */}
        <div style={{ display: 'flex', gap: 4, minWidth: 0, overflowX: 'auto' }}>
          {canExtra('contact') && tabBtn('contact', t('mod_contact'))}
          {canExtra('services') && tabBtn('services', t('mod_services'))}
          {tabBtn('quotation', t('pk_quotation'))}{tabBtn('invoice', t('pk_invoice'))}{tabBtn('receipt', t('pk_receipt'))}
          {canExtra('bookkeeping') && tabBtn('bookkeeping', t('mod_bookkeeping'))}
        </div>
      </div>
      {tab === 'contact' ? <Contact embedded />
        : tab === 'services' ? <Services embedded />
        : tab === 'bookkeeping' ? <Bookkeeping embedded />
        : tab === 'invoice' ? <InvoiceList initialOpen={paramTab !== 'quotation' && paramTab !== 'receipt' ? openId : undefined} />
        : tab === 'quotation' ? <QuotationList initialOpen={paramTab === 'quotation' ? openId : undefined} />
        : <ReceiptList initialOpen={paramTab === 'receipt' ? openId : undefined} />}
    </div>
  );
}

// ── Standalone page (/m/peak) ────────────────────────────────────────────────

// ── Row actions dropdown ─────────────────────────────────────────────────────
// The Quotation / Invoice / Receipt rows used to carry up to five icon buttons each, which crowded
// the last column and made the destructive one sit a few pixels from the harmless ones. They are now
// one "⋮" per row. position:fixed (not absolute) because the table scrolls inside its container —
// an absolutely positioned menu is clipped on the last rows.
type RowAction = { label: string; icon: string; onClick: () => void; danger?: boolean; busy?: boolean; disabled?: boolean };
function RowMenu({ actions, title }: { actions: RowAction[]; title: string }) {
  const [at, setAt] = useState<{ x: number; y: number } | null>(null);
  const open = !!at;
  const boxRef = useRef<HTMLDivElement | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const items = actions.filter(Boolean);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node) && !btnRef.current?.contains(e.target as Node)) setAt(null); };
    const close = () => setAt(null);
    document.addEventListener('mousedown', away);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => { document.removeEventListener('mousedown', away); window.removeEventListener('scroll', close, true); window.removeEventListener('resize', close); };
  }, [open]);
  if (!items.length) return null;
  const busy = items.some(a => a.busy);
  return (
    <>
      <button ref={btnRef} title={title} onClick={e => {
        e.stopPropagation();
        if (open) { setAt(null); return; }
        const r = btnRef.current?.getBoundingClientRect();
        setAt(r ? { x: r.right, y: r.bottom } : { x: 0, y: 0 });
      }} style={{ width: 26, height: 26, borderRadius: 6, border: '1px solid var(--border-strong)', background: open ? 'var(--bg-active)' : 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 15, lineHeight: 1, verticalAlign: 'middle' }}>
        <i className={busy ? 'ti ti-loader' : 'ti ti-dots-vertical'} />
      </button>
      {open && (
        <div ref={boxRef} onClick={e => e.stopPropagation()} style={{ position: 'fixed', top: at!.y + 4, left: Math.max(8, at!.x - 210), width: 210, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 10, boxShadow: 'var(--shadow-md)', zIndex: 10050, overflow: 'hidden', padding: '4px 0', textAlign: 'left' }}>
          {items.map((a, i) => (
            <button key={i} disabled={a.disabled || a.busy}
              onClick={() => { setAt(null); a.onClick(); }}
              style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', textAlign: 'left', padding: '8px 12px', border: 'none', background: 'transparent', cursor: a.disabled || a.busy ? 'default' : 'pointer', fontSize: 12.5, fontWeight: 500, color: a.danger ? '#E24B4A' : 'var(--text-primary)', opacity: a.disabled ? 0.5 : 1 }}
              onMouseEnter={e => { if (!a.disabled && !a.busy) e.currentTarget.style.background = 'var(--bg-active)'; }}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
              <i className={a.busy ? 'ti ti-loader' : `ti ti-${a.icon}`} style={{ fontSize: 14, width: 16, textAlign: 'center' }} />{a.label}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

// Deleting a receipt: ask the server what the delete would actually do, refuse or warn accordingly,
// then collect the mandatory reason. Two things the operator must know BEFORE typing a reason:
// an already-emailed receipt is admin-only, and a matched bank line will be released back to the
// unmatched queue. Shared by all three delete entry points so they can never diverge.
async function deleteReceiptFlow(id: string, reload: () => void) {
  let impact: any = null;
  try { impact = await api.receiptDeleteImpact(id); } catch (e: any) { alert(e.message); return; }
  if (impact.blocked) {
    alert(`Receipt ${impact.docNo} has already been emailed to the client.\n\nOnly an administrator can delete it — its number is never reissued and the client already holds that document.`);
    return;
  }
  const warn: string[] = [];
  if (impact.emailed) warn.push(`⚠️ This receipt has already been EMAILED to the client. Deleting it does not recall that email; the number ${impact.docNo} will never be reissued.`);
  if (impact.bankTxns?.length) {
    const list = impact.bankTxns.map((t: any) => `   • ${t.date} · ฿${Number(t.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`).join('\n');
    warn.push(`⚠️ ${impact.bankTxns.length} matched bank transaction(s) will be UNMATCHED and returned to the pending queue:\n${list}`);
  }
  if (warn.length && !window.confirm(`Delete receipt ${impact.docNo}?\n\n${warn.join('\n\n')}\n\nContinue?`)) return;
  const reason = await askDeleteReason(impact.docNo);
  if (reason === null) return;
  try { await api.deleteReceipt(id, reason); reload(); } catch (e: any) { alert(e.message); }
}

// The audit log wants comparable reasons, not free prose — so the operator picks from a fixed list,
// with "Other reason" opening a required detail box. Plain DOM (not React) because deleteReceiptFlow
// is a shared function called from three different components; resolves null on cancel.
const DELETE_REASONS = ['Issued by mistake', 'Cancel by client', 'Other reason'];
function askDeleteReason(docNo: string): Promise<string | null> {
  return new Promise(resolve => {
    const wrap = document.createElement('div');
    wrap.style.cssText = 'position:fixed;inset:0;z-index:9999;background:rgba(15,23,42,0.4);display:flex;align-items:center;justify-content:center';
    wrap.innerHTML = `
      <div style="width:420px;max-width:92vw;background:var(--bg-panel,#fff);border-radius:12px;box-shadow:0 12px 40px rgba(0,0,0,0.25);padding:20px 22px;font-family:inherit">
        <div style="font-size:14px;font-weight:700;color:var(--text-primary,#111)">Deleting ${docNo}</div>
        <div style="font-size:12.5px;color:var(--text-secondary,#555);margin-top:6px;line-height:1.5">This is recorded in the audit log.<br>Why is it being deleted?</div>
        <select id="delReasonSel" style="width:100%;margin-top:14px;padding:8px 10px;font-size:13px;border-radius:8px;border:1px solid var(--border,#ddd);background:var(--bg-app,#fafafa);color:var(--text-primary,#111)">
          ${DELETE_REASONS.map(r => `<option value="${r}">${r}</option>`).join('')}
        </select>
        <input id="delReasonOther" placeholder="Please describe the reason…" style="display:none;width:100%;margin-top:8px;padding:8px 10px;font-size:13px;border-radius:8px;border:1px solid var(--border,#ddd);background:var(--bg-app,#fafafa);color:var(--text-primary,#111);box-sizing:border-box" />
        <div style="display:flex;justify-content:flex-end;gap:10px;margin-top:18px">
          <button id="delReasonCancel" style="padding:7px 16px;font-size:13px;border-radius:8px;border:1px solid var(--border,#ddd);background:transparent;color:var(--text-secondary,#555);cursor:pointer">Cancel</button>
          <button id="delReasonOk" style="padding:7px 18px;font-size:13px;font-weight:600;border-radius:8px;border:none;background:#B42318;color:#fff;cursor:pointer">Delete</button>
        </div>
      </div>`;
    document.body.appendChild(wrap);
    const sel = wrap.querySelector('#delReasonSel') as HTMLSelectElement;
    const other = wrap.querySelector('#delReasonOther') as HTMLInputElement;
    sel.onchange = () => {
      const isOther = sel.value === 'Other reason';
      other.style.display = isOther ? 'block' : 'none';
      if (isOther) other.focus();
    };
    const done = (v: string | null) => { document.body.removeChild(wrap); resolve(v); };
    (wrap.querySelector('#delReasonCancel') as HTMLButtonElement).onclick = () => done(null);
    wrap.onclick = e => { if (e.target === wrap) done(null); };
    (wrap.querySelector('#delReasonOk') as HTMLButtonElement).onclick = () => {
      if (sel.value !== 'Other reason') return done(sel.value);
      const detail = other.value.trim();
      if (!detail) { other.style.borderColor = '#B42318'; other.focus(); return; }
      done(`Other — ${detail}`);
    };
  });
}

export default function Peak() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-app)' }}>
      <PeakView header={<>
        <button onClick={() => navigate('/')} title="SLF" style={{ width: 30, height: 30, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', alignSelf: 'center', marginBottom: 6, flexShrink: 0 }}>
          <img className="app-logo" src="/logo.png" alt="SLF" style={{ width: 28, height: 28, objectFit: 'contain' }} />
        </button>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6, whiteSpace: 'nowrap', flexShrink: 0 }}>{t('mod_peak')}</div>
      </>} />
    </div>
  );
}
